import {
  Avatar,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Row,
  Select,
  Space,
  Tabs,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { UploadProps } from 'antd';
import {
  BellOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  LikeOutlined,
  PaperClipOutlined,
  QuestionCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { IncidentSeverityTag, IncidentStatusTag, IncidentTaskStatusTag } from '../components/StatusTag';
import { ProcessTimelineChart } from '../components/ProcessTimelineChart';
import ProductFormRenderer from '../components/ProductFormRenderer';
import type {
  Incident,
  AuditEdit,
  IncidentAttachment,
  IncidentFieldChange,
  IncidentComment,
  IncidentTaskInternalComment,
  IncidentSeverity,
  IncidentUpdate,
  CurrentUser,
  DefectTag,
  TaskActivity,
  TaskActivityUpdate,
  TaskHoldReason,
  IncidentRequest,
  IncidentRequestActivity,
  IncidentRequestActivityUpdate,
  IncidentRequestCreate,
  IncidentRequestStatus,
  IncidentRequestUpdate,
  IncidentTask,
  IncidentTaskStatus,
  IncidentTaskUpdate,
  IncidentFormField,
  IncidentFormSchema,
  IncidentTranslations,
  LocalizedText,
  ProcessTimeline,
  UserRole,
} from '../types/incident';
import { canEditIncident, canEditTask as canEditTaskByAssignment } from '../assignments';
import { getIncident, getIncidentTranslations, listDefectTags } from '../services/api';

const { Paragraph, Text } = Typography;
const { TextArea } = Input;

interface Props {
  incidents: Incident[];
  attachmentsByIncident: Record<string, IncidentAttachment[]>;
  currentUser: CurrentUser | null;
  onAddComment: (incidentId: string, body: string) => Promise<IncidentComment>;
  onUpdateComment: (incidentId: string, commentId: string, body: string) => Promise<IncidentComment>;
  onDeleteComment: (incidentId: string, commentId: string) => Promise<void>;
  onAddTaskInternalComment: (incidentId: string, taskId: string, body: string) => Promise<IncidentTaskInternalComment>;
  onUpdateTaskInternalComment: (
    incidentId: string,
    taskId: string,
    commentId: string,
    body: string
  ) => Promise<IncidentTaskInternalComment>;
  onDeleteTaskInternalComment: (incidentId: string, taskId: string, commentId: string) => Promise<void>;
  onAttachFile: (incidentId: string, file: File) => Promise<IncidentAttachment>;
  onDeleteAttachment: (incidentId: string, attachmentId: string) => Promise<void>;
  onCreateAttachmentDownload: (incidentId: string, attachmentId: string) => Promise<string>;
  onCreateAttachmentThumbnailDownload: (incidentId: string, attachmentId: string) => Promise<string>;
  onLoadAttachments: (incidentId: string) => Promise<IncidentAttachment[]>;
  onLoadComments: (incidentId: string) => Promise<IncidentComment[]>;
  onLoadTaskInternalComments: (incidentId: string, taskId: string) => Promise<IncidentTaskInternalComment[]>;
  onLoadTasks: (incidentId: string) => Promise<IncidentTask[]>;
  onLoadRequests: (incidentId: string) => Promise<IncidentRequest[]>;
  onLoadTaskActivities: (incidentId: string) => Promise<TaskActivity[]>;
  onLoadProcessTimeline: (incidentId: string) => Promise<ProcessTimeline | null>;
  onLoadFieldChanges: (incidentId: string) => Promise<IncidentFieldChange[]>;
  onDeleteTaskActivity: (incidentId: string, activityId: string) => Promise<void>;
  onUpdateTaskActivity: (incidentId: string, activityId: string, payload: TaskActivityUpdate) => Promise<TaskActivity>;
  onLoadTaskActivityEdits: (incidentId: string, activityId: string) => Promise<AuditEdit[]>;
  onCreateRequest: (incidentId: string, payload: IncidentRequestCreate) => Promise<IncidentRequest>;
  onUpdateRequest: (incidentId: string, requestId: string, payload: IncidentRequestUpdate) => Promise<IncidentRequest>;
  onLoadRequestEdits: (incidentId: string, requestId: string) => Promise<AuditEdit[]>;
  onUpdateRequestActivity: (
    incidentId: string,
    requestId: string,
    activityId: string,
    payload: IncidentRequestActivityUpdate
  ) => Promise<IncidentRequestActivity>;
  onLoadRequestActivityEdits: (incidentId: string, requestId: string, activityId: string) => Promise<AuditEdit[]>;
  onUpdateTask: (incidentId: string, taskId: string, payload: IncidentTaskUpdate) => Promise<IncidentTask>;
  onUpdateIncident: (incidentId: string, payload: IncidentUpdate) => Promise<Incident>;
  onSetIncidentWatch: (incidentId: string, enabled: boolean) => Promise<Incident>;
  onSetIncidentUseful: (incidentId: string, enabled: boolean) => Promise<Incident>;
  onCloseIncident: (incidentId: string, comment: string) => Promise<Incident>;
}

type IncidentEditFormValues = Omit<
  IncidentUpdate,
  'commissioning_date' | 'trouble_date' | 'claim_receive_date' | 'production_factory_setting_date' | 'defect_tag_ids'
> & {
  commissioning_date?: Dayjs | null;
  trouble_date?: Dayjs | null;
  claim_receive_date?: Dayjs | null;
  production_factory_setting_date?: Dayjs | null;
  defect_location_tag_ids?: string[];
  defect_phenomenon_tag_ids?: string[];
  product_form_response?: Record<string, unknown>;
};

type PartsReturnDraft = {
  part_name?: string;
  part_number?: string;
  quantity?: number | null;
  return_reason?: string;
  ship_to?: string;
  shipping_instruction?: string;
};

type AuditEditTarget =
  | { type: 'taskActivity'; activity: TaskActivity }
  | { type: 'request'; request: IncidentRequest }
  | { type: 'requestActivity'; request: IncidentRequest; activity: IncidentRequestActivity };
type TaskActivityEditField = 'body' | 'root_cause' | 'countermeasure' | 'requested_action';
type WorkflowRole = Exclude<UserRole, 'ORG_ADMIN' | 'SYSTEM_ADMIN'>;

const severityOptions: IncidentSeverity[] = ['low', 'medium', 'high', 'critical'];
const taskStatusOptions: IncidentTaskStatus[] = ['OPEN', 'IN_PROGRESS', 'ON_HOLD', 'RESOLVED', 'CLOSED'];
const deletableActivityTypes = ['INTERIM_RESPONSE', 'FINAL_RESPONSE', 'REQUEST_ANSWERED', 'REQUEST_REVIEWED', 'ON_HOLD_REQUESTED'];
const adminRoles: UserRole[] = ['ORG_ADMIN', 'SYSTEM_ADMIN'];
const requestableWorkflowRoles: WorkflowRole[] = ['ISSUING_PARTY', 'SERVICE', 'QUALITY'];
const primaryResponseTaskTypes: IncidentTask['task_type'][] = ['SERVICE_RESPONSE', 'QUALITY_RESPONSE'];
const issuingPartyConfirmationTaskType: IncidentTask['task_type'] = 'ISSUING_PARTY_CONFIRMATION';
const holdReasonByRole: Record<WorkflowRole, TaskHoldReason> = {
  ISSUING_PARTY: 'AWAITING_ISSUING_PARTY',
  SERVICE: 'AWAITING_SERVICE',
  QUALITY: 'AWAITING_QUALITY',
};
const roleByHoldReason: Partial<Record<TaskHoldReason, WorkflowRole>> = {
  AWAITING_ISSUING_PARTY: 'ISSUING_PARTY',
  AWAITING_SERVICE: 'SERVICE',
  AWAITING_QUALITY: 'QUALITY',
};

const formatBytes = (sizeBytes: number): string => {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`;
};

const isImageAttachment = (attachment: IncidentAttachment) => attachment.content_type.startsWith('image/');
const isPdfAttachment = (attachment: IncidentAttachment) => attachment.content_type === 'application/pdf';
const isTextAttachment = (attachment: IncidentAttachment) =>
  attachment.content_type.startsWith('text/') ||
  attachment.content_type === 'application/json' ||
  attachment.content_type === 'application/xml' ||
  attachment.file_name.toLowerCase().endsWith('.csv');
const isOfficeAttachment = (attachment: IncidentAttachment) =>
  /word|excel|spreadsheet|officedocument|msword|ms-excel/i.test(attachment.content_type) ||
  /\.(doc|docx|xls|xlsx)$/i.test(attachment.file_name);
const canPreviewAttachment = (attachment: IncidentAttachment) =>
  isImageAttachment(attachment) || isPdfAttachment(attachment) || isTextAttachment(attachment);
const hasUploadedObjectKey = (attachment: IncidentAttachment) =>
  new RegExp(`^incidents/${attachment.incident_id}/[0-9a-f-]{36}-.+`, 'i').test(attachment.object_key);
const hasReadyThumbnail = (attachment: IncidentAttachment) =>
  attachment.thumbnail_status === 'READY' && Boolean(attachment.thumbnail_object_key);
const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const attachmentIcon = (attachment: IncidentAttachment) => {
  if (isImageAttachment(attachment)) return <FileImageOutlined />;
  if (isPdfAttachment(attachment)) return <FilePdfOutlined />;
  if (isTextAttachment(attachment)) return <FileTextOutlined />;
  if (/excel|spreadsheet|ms-excel/i.test(attachment.content_type) || /\.(xls|xlsx)$/i.test(attachment.file_name)) {
    return <FileExcelOutlined />;
  }
  if (/word|msword/i.test(attachment.content_type) || /\.(doc|docx)$/i.test(attachment.file_name)) {
    return <FileWordOutlined />;
  }
  return <FileOutlined />;
};

const IncidentDetailPage = ({
  incidents,
  attachmentsByIncident,
  currentUser,
  onAddComment,
  onUpdateComment,
  onDeleteComment,
  onAddTaskInternalComment,
  onUpdateTaskInternalComment,
  onDeleteTaskInternalComment,
  onAttachFile,
  onDeleteAttachment,
  onCreateAttachmentDownload,
  onCreateAttachmentThumbnailDownload,
  onLoadAttachments,
  onLoadComments,
  onLoadTaskInternalComments,
  onLoadTasks,
  onLoadRequests,
  onLoadTaskActivities,
  onLoadProcessTimeline,
  onLoadFieldChanges,
  onDeleteTaskActivity,
  onUpdateTaskActivity,
  onLoadTaskActivityEdits,
  onCreateRequest,
  onUpdateRequest,
  onLoadRequestEdits,
  onUpdateRequestActivity,
  onLoadRequestActivityEdits,
  onUpdateTask,
  onUpdateIncident,
  onSetIncidentWatch,
  onSetIncidentUseful,
  onCloseIncident,
}: Props) => {
  const { i18n, t } = useTranslation();
  const { incidentId } = useParams();
  const baseIncident = incidents.find((item) => item.id === incidentId);
  const [remoteIncident, setRemoteIncident] = useState<Incident | null>(null);
  const incident = remoteIncident?.id === incidentId ? remoteIncident : baseIncident;
  const [form] = Form.useForm<IncidentEditFormValues>();
  const [messageApi, messageContextHolder] = message.useMessage();
  const [comments, setComments] = useState<IncidentComment[]>([]);
  const [commentBody, setCommentBody] = useState('');
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentBody, setEditingCommentBody] = useState('');
  const [commentActionId, setCommentActionId] = useState<string | null>(null);
  const [taskInternalComments, setTaskInternalComments] = useState<Record<string, IncidentTaskInternalComment[]>>({});
  const [taskInternalCommentBodies, setTaskInternalCommentBodies] = useState<Record<string, string>>({});
  const [taskInternalCommentSubmittingId, setTaskInternalCommentSubmittingId] = useState<string | null>(null);
  const [editingTaskInternalCommentId, setEditingTaskInternalCommentId] = useState<string | null>(null);
  const [editingTaskInternalCommentBody, setEditingTaskInternalCommentBody] = useState('');
  const [taskInternalCommentActionId, setTaskInternalCommentActionId] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeComment, setCloseComment] = useState('');
  const [closeSaving, setCloseSaving] = useState(false);
  const [taskSavingId, setTaskSavingId] = useState<string | null>(null);
  const [activeTabKey, setActiveTabKey] = useState('details');
  const [activityDeletingId, setActivityDeletingId] = useState<string | null>(null);
  const [attachmentDeletingId, setAttachmentDeletingId] = useState<string | null>(null);
  const [previewAttachment, setPreviewAttachment] = useState<IncidentAttachment | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [attachmentPreviewUrls, setAttachmentPreviewUrls] = useState<Record<string, string>>({});
  const [tasks, setTasks] = useState<IncidentTask[]>([]);
  const [requests, setRequests] = useState<IncidentRequest[]>([]);
  const [requestDrafts, setRequestDrafts] = useState<Record<string, string>>({});
  const [taskResponseDrafts, setTaskResponseDrafts] = useState<Record<string, string>>({});
  const [partsReturnDrafts, setPartsReturnDrafts] = useState<Record<string, PartsReturnDraft>>({});
  const [requestActionId, setRequestActionId] = useState<string | null>(null);
  const [taskActivities, setTaskActivities] = useState<TaskActivity[]>([]);
  const [auditEditTarget, setAuditEditTarget] = useState<AuditEditTarget | null>(null);
  const [auditEditValues, setAuditEditValues] = useState<Record<string, string>>({});
  const [auditEditReason, setAuditEditReason] = useState('');
  const [auditEditSaving, setAuditEditSaving] = useState(false);
  const [auditHistoryTarget, setAuditHistoryTarget] = useState<AuditEditTarget | null>(null);
  const [auditHistory, setAuditHistory] = useState<AuditEdit[]>([]);
  const [auditHistoryLoading, setAuditHistoryLoading] = useState(false);
  const [processTimeline, setProcessTimeline] = useState<ProcessTimeline | null>(null);
  const [fieldChanges, setFieldChanges] = useState<IncidentFieldChange[]>([]);
  const [attachments, setAttachments] = useState<IncidentAttachment[]>([]);
  const [defectTags, setDefectTags] = useState<DefectTag[]>([]);
  const [defectTagsLoading, setDefectTagsLoading] = useState(false);
  const [translations, setTranslations] = useState<IncidentTranslations | null>(null);
  const [reactionSaving, setReactionSaving] = useState<'watch' | 'useful' | null>(null);

  const currentIncidentId = incident?.id;
  const currentLanguage = i18n.language.startsWith('ja') ? 'ja' : i18n.language;
  const localized = (entityType: string, entityId: string, fieldName: string, original: string | null | undefined) => {
    const item = translations?.items?.[`${entityType}:${entityId}:${fieldName}`];
    return item || {
      entity_type: entityType,
      entity_id: entityId,
      field_name: fieldName,
      text: original || '',
      language: currentLanguage,
      source_language: currentLanguage,
      source_language_confidence: null,
      source_language_detection_method: 'ui_fallback',
      is_translated: false,
      translation_status: 'ORIGINAL',
      original_text: original || '',
    } satisfies LocalizedText;
  };
  const renderLocalizedText = (
    entityType: string,
    entityId: string,
    fieldName: string,
    original: string | null | undefined,
    options?: { empty?: string; paragraph?: boolean }
  ) => {
    const value = localized(entityType, entityId, fieldName, original);
    const text = value.text || options?.empty || '-';
    const content = (
      <Space direction="vertical" size={4} className="localizedTextBlock">
        <span>{text}</span>
        {value.is_translated && (
          <Space size={6} className="translationMeta">
            <Tag color="blue">{t('translation.autoTranslated')}</Tag>
            <details>
              <summary>{t('translation.showOriginal')}</summary>
              <Text type="secondary">{value.original_text}</Text>
            </details>
          </Space>
        )}
        {!value.is_translated && value.translation_status === 'PENDING' && (
          <Tag>{t('translation.pending')}</Tag>
        )}
      </Space>
    );
    return options?.paragraph ? <Paragraph>{content}</Paragraph> : content;
  };
  const localizedPlainText = (entityType: string, entityId: string, fieldName: string, original: string | null | undefined) =>
    localized(entityType, entityId, fieldName, original).text || '-';
  const requestDisplayTitle = (request: IncidentRequest) => {
    if (request.request_type === 'PARTS_RETURN' && request.title === '部品返却依頼') {
      return t('incident.detail.requests.partsReturnTitle');
    }
    return localizedPlainText('incident_request', request.id, 'title', request.title);
  };

  useEffect(() => {
    if (!incidentId || !isUuid(incidentId)) {
      setRemoteIncident(null);
      return;
    }
    let cancelled = false;
    getIncident(incidentId)
      .then((item) => {
        if (!cancelled) setRemoteIncident(item);
      })
      .catch(() => {
        if (!cancelled) setRemoteIncident(null);
      });
    return () => {
      cancelled = true;
    };
  }, [incidentId]);
  const translateKnownValue = (baseKey: string, value: string | null | undefined) =>
    value ? t(`${baseKey}.${value}`, { defaultValue: value }) : '-';
  const languageName = (language: string | null | undefined) => {
    const normalized = language || 'ja';
    return t(`translation.languages.${normalized}`, { defaultValue: normalized });
  };
  const taskInputLanguage = (task: IncidentTask) => (
    task.responsible_role === 'ISSUING_PARTY'
      ? incident?.primary_language || currentLanguage
      : currentUser?.preferred_ui_language || currentLanguage
  );
  const renderTaskInputLanguageHint = (task: IncidentTask) => (
    <Text type="secondary" className="taskInputLanguageHint">
      {t(
        task.responsible_role === 'ISSUING_PARTY'
          ? 'incident.detail.tasks.issuingPartyInputLanguageHint'
          : 'incident.detail.tasks.assigneeInputLanguageHint',
        { language: languageName(taskInputLanguage(task)) }
      )}
    </Text>
  );
  const formatProductFormValue = (field: IncidentFormField, value: unknown) => {
    if (field.type === 'checkbox') return value ? t('common.yes') : t('common.no');
    if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '-';
    if (value === null || value === undefined || value === '') return '-';
    return String(value);
  };
  const hydrateProductFormResponse = (schema: IncidentFormSchema | null | undefined, response?: Record<string, unknown>) => {
    if (!schema) return {};
    return schema.fields.reduce<Record<string, unknown>>((acc, field) => {
      if (field.type === 'section') return acc;
      const value = response?.[field.id];
      acc[field.id] = field.type === 'date' && typeof value === 'string' && value ? dayjs(value) : value;
      return acc;
    }, {});
  };
  const normalizeProductFormResponse = (
    schema: IncidentFormSchema | null | undefined,
    response?: Record<string, unknown>
  ): Record<string, unknown> => {
    if (!schema) return {};
    return schema.fields.reduce<Record<string, unknown>>((acc, field) => {
      if (field.type === 'section') return acc;
      const value = response?.[field.id];
      if (value === undefined || value === null || value === '') return acc;
      acc[field.id] = field.type === 'date' && dayjs.isDayjs(value) ? value.format('YYYY-MM-DD') : value;
      return acc;
    }, {});
  };
  const renderProductFormSnapshot = () => {
    const schema = incident?.product_form_schema_snapshot as IncidentFormSchema | null | undefined;
    const fields = schema?.fields || [];
    if (fields.length === 0) return null;

    const response = incident?.product_form_response || {};
    return (
      <Card title={t('incident.detail.sections.productSpecific')}>
        <Descriptions bordered column={1} size="small">
          {fields.map((field) =>
            field.type === 'section' ? (
              <Descriptions.Item key={field.id} label={field.label} className="productFormSectionDescription">
                {field.description || '-'}
              </Descriptions.Item>
            ) : (
              <Descriptions.Item key={field.id} label={field.label}>
                {formatProductFormValue(field, response[field.id])}
              </Descriptions.Item>
            )
          )}
        </Descriptions>
      </Card>
    );
  };
  const userAssignments = useMemo(() => currentUser?.assignments || [], [currentUser?.assignments]);
  const canEditIncidentBody = canEditIncident(userAssignments);
  const canEditTask = (task: IncidentTask) => canEditTaskByAssignment(userAssignments, task);
  const hasRole = useCallback(
    (role: UserRole) => userAssignments.some((assignment) => assignment.role === role || adminRoles.includes(assignment.role)),
    [userAssignments]
  );
  const canViewTaskInternalComments = useCallback(
    (task: IncidentTask) => ['SERVICE', 'QUALITY'].includes(task.responsible_role) && hasRole(task.responsible_role),
    [hasRole]
  );
  const canUpdateRequest = (request: IncidentRequest) => hasRole(request.source_role) || hasRole(request.target_role);
  const canAnswerRequest = (request: IncidentRequest) => hasRole(request.target_role);
  const canCloseRequest = (request: IncidentRequest) => hasRole(request.source_role) && request.status === 'RESOLVED';
  const canManageAuditItem = (actorUserId?: string | null) =>
    actorUserId === currentUser?.id || userAssignments.some((assignment) => adminRoles.includes(assignment.role));
  const getEditableTaskActivityFields = (activity: TaskActivity): TaskActivityEditField[] => {
    if (activity.activity_type === 'FINAL_RESPONSE') return ['root_cause', 'countermeasure'];
    if (activity.activity_type === 'ON_HOLD_REQUESTED') return ['requested_action'];
    if (['INTERIM_RESPONSE', 'REQUEST_ANSWERED'].includes(activity.activity_type)) return ['body'];
    return [];
  };
  const hasEditableTaskActivityContent = (activity: TaskActivity) => getEditableTaskActivityFields(activity).length > 0;
  const hasEditableRequestActivityContent = (activity: IncidentRequestActivity) =>
    activity.activity_type !== 'REQUEST_CREATED' && Boolean(activity.body);
  const getCommentAuthorName = (comment: IncidentComment) =>
    comment.author_display_name ||
    (comment.author_user_id === currentUser?.id ? currentUser?.display_name : null) ||
    t('incident.detail.comments.unknownAuthor');
  const getCommentAuthorInitial = (name: string) => name.trim().charAt(0).toUpperCase() || '?';
  const renderCommentAvatar = (comment: IncidentComment) => {
    const authorName = getCommentAuthorName(comment);
    return (
      <Avatar className="commentAvatar" src={comment.author_profile_image_url || undefined}>
        {getCommentAuthorInitial(authorName)}
      </Avatar>
    );
  };

  useEffect(() => {
    if (!currentIncidentId) return;
    let cancelled = false;
    Promise.all([
      onLoadComments(currentIncidentId),
      onLoadAttachments(currentIncidentId),
      onLoadTasks(currentIncidentId),
      onLoadRequests(currentIncidentId),
      onLoadTaskActivities(currentIncidentId),
      onLoadFieldChanges(currentIncidentId),
      onLoadProcessTimeline(currentIncidentId),
    ]).then(
      ([commentItems, attachmentItems, taskItems, requestItems, activityItems, fieldChangeItems, timeline]) => {
        if (!cancelled) {
          setComments(commentItems);
          setAttachments(attachmentItems);
          setTasks(taskItems);
          setRequests(requestItems);
          setTaskActivities(activityItems);
          setFieldChanges(fieldChangeItems);
          setProcessTimeline(timeline);
          const visibleInternalCommentTasks = taskItems.filter(canViewTaskInternalComments);
          setTaskInternalComments({});
          Promise.all(
            visibleInternalCommentTasks.map(async (task) => {
              const items = await onLoadTaskInternalComments(currentIncidentId, task.id);
              return [task.id, items] as const;
            })
          ).then((items) => {
            if (!cancelled) setTaskInternalComments(Object.fromEntries(items));
          });
        }
      }
    );
    return () => {
      cancelled = true;
    };
  }, [canViewTaskInternalComments, currentIncidentId, onLoadAttachments, onLoadComments, onLoadFieldChanges, onLoadProcessTimeline, onLoadRequests, onLoadTaskActivities, onLoadTaskInternalComments, onLoadTasks]);

  useEffect(() => {
    if (!currentIncidentId) {
      setTranslations(null);
      return;
    }
    let cancelled = false;
    getIncidentTranslations(currentIncidentId, currentLanguage)
      .then((items) => {
        if (!cancelled) setTranslations(items);
      })
      .catch(() => {
        if (!cancelled) setTranslations(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currentIncidentId, currentLanguage]);

  useEffect(() => {
    let cancelled = false;
    setDefectTagsLoading(true);
    listDefectTags()
      .then((items) => {
        if (!cancelled) setDefectTags(items);
      })
      .catch(() => {
        if (!cancelled) messageApi.warning(t('incident.create.messages.defectTagsUnavailable'));
      })
      .finally(() => {
        if (!cancelled) setDefectTagsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [messageApi, t]);

  useEffect(() => {
    let cancelled = false;
    const thumbnailAttachments = attachments.filter(
      (attachment) => hasReadyThumbnail(attachment) && hasUploadedObjectKey(attachment)
    );
    if (thumbnailAttachments.length === 0) {
      setAttachmentPreviewUrls({});
      return () => {
        cancelled = true;
      };
    }
    Promise.all(
      thumbnailAttachments.map(async (attachment) => {
        try {
          const url = await onCreateAttachmentThumbnailDownload(attachment.incident_id, attachment.id);
          return [attachment.id, url] as const;
        } catch {
          return [attachment.id, null] as const;
        }
      })
    ).then((items) => {
      if (cancelled) return;
      setAttachmentPreviewUrls(
        Object.fromEntries(items.filter((item): item is readonly [string, string] => Boolean(item[1])))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [attachments, onCreateAttachmentThumbnailDownload]);

  useEffect(() => {
    if (!currentIncidentId) return;
    const hasPendingThumbnail = attachments.some(
      (attachment) => attachment.thumbnail_status === 'PENDING' || attachment.thumbnail_status === 'PROCESSING'
    );
    if (!hasPendingThumbnail) return;

    let cancelled = false;
    let attempts = 0;
    const intervalId = window.setInterval(async () => {
      attempts += 1;
      try {
        const attachmentItems = await onLoadAttachments(currentIncidentId);
        const currentStatusById = new Map(attachments.map((attachment) => [attachment.id, attachment.thumbnail_status]));
        const hasStatusChange = attachmentItems.some(
          (attachment) => currentStatusById.get(attachment.id) !== attachment.thumbnail_status
        );
        if (!cancelled && hasStatusChange) setAttachments(attachmentItems);
      } catch {
        window.clearInterval(intervalId);
      }
      if (attempts >= 5) window.clearInterval(intervalId);
    }, 2000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [attachments, currentIncidentId, onLoadAttachments]);

  const tagLabel = (tag: DefectTag) => (i18n.language.startsWith('ja') ? tag.label_ja : tag.label_en);
  const defectLocationTagOptions = defectTags
    .filter((tag) => tag.category === 'LOCATION')
    .map((tag) => ({ value: tag.id, label: tagLabel(tag) }));
  const defectPhenomenonTagOptions = defectTags
    .filter((tag) => tag.category === 'PHENOMENON')
    .map((tag) => ({ value: tag.id, label: tagLabel(tag) }));
  const renderDefectTags = (category: DefectTag['category']) => {
    if (!incident) return '-';
    const tags = (incident.defect_tags || []).filter((tag) => tag.category === category);
    if (tags.length === 0) return '-';
    return (
      <Space wrap size={[4, 4]}>
        {tags.map((tag) => (
          <Tag key={tag.id}>{tagLabel(tag)}</Tag>
        ))}
      </Space>
    );
  };

  const openEditDrawer = () => {
    if (!incident) return;
    const productFormSchema = incident.product_form_schema_snapshot as IncidentFormSchema | null | undefined;
    form.setFieldsValue({
      title: incident.title,
      severity: incident.severity,
      trouble_date: incident.trouble_date ? dayjs(incident.trouble_date) : null,
      claim_receive_date: incident.claim_receive_date ? dayjs(incident.claim_receive_date) : null,
      commissioning_date: incident.commissioning_date ? dayjs(incident.commissioning_date) : null,
      production_factory_setting_date: incident.production_factory_setting_date ? dayjs(incident.production_factory_setting_date) : null,
      info_classification: incident.info_classification,
      claim_class: incident.claim_class,
      related_gib_no: incident.related_gib_no || null,
      site_resolution_status: incident.site_resolution_status || null,
      initial_comment: incident.initial_comment,
      product_classification: incident.product_classification,
      defect_location: incident.defect_location || null,
      installation_environment: incident.installation_environment,
      model_name: incident.model_name,
      model_number: incident.model_number || undefined,
      serial_no: incident.serial_no || undefined,
      lot_number: incident.lot_number || undefined,
      is_new_product: incident.is_new_product ?? null,
      unit_count: incident.unit_count ?? null,
      production_factory: incident.production_factory || null,
      season: incident.season || null,
      piping_length: incident.piping_length || null,
      error_code: incident.error_code || null,
      room_size: incident.room_size || null,
      dealer: incident.dealer || null,
      delivery_destination: incident.delivery_destination || null,
      installer_name: incident.installer_name || null,
      delivery_route: incident.delivery_route || null,
      service_control_no: incident.service_control_no || null,
      customer_impact: incident.customer_impact || null,
      phenomenon: incident.phenomenon,
      temporary_action: incident.temporary_action || undefined,
      defect_location_tag_ids: (incident.defect_tags || []).filter((tag) => tag.category === 'LOCATION').map((tag) => tag.id),
      defect_phenomenon_tag_ids: (incident.defect_tags || []).filter((tag) => tag.category === 'PHENOMENON').map((tag) => tag.id),
      product_form_response: hydrateProductFormResponse(productFormSchema, incident.product_form_response),
    });
    setEditOpen(true);
  };

  const normalizeOptionalText = (value: string | null | undefined): string | null => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  };

  const normalizePatchText = (value: string | null | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
  };

  const formatDate = (value: Dayjs | null | undefined): string | null =>
    value ? value.format('YYYY-MM-DD') : null;

  const validateEditDateOrder = () => {
    const values = form.getFieldsValue();
    const commissioningDate = values.commissioning_date;
    const troubleDate = values.trouble_date;
    const claimReceiveDate = values.claim_receive_date;
    if (commissioningDate && troubleDate && troubleDate.isBefore(commissioningDate, 'day')) {
      return Promise.reject(new Error(t('incident.create.messages.troubleDateOrder')));
    }
    if (troubleDate && claimReceiveDate && claimReceiveDate.isBefore(troubleDate, 'day')) {
      return Promise.reject(new Error(t('incident.create.messages.claimReceiveDateOrder')));
    }
    if (commissioningDate && claimReceiveDate && claimReceiveDate.isBefore(commissioningDate, 'day')) {
      return Promise.reject(new Error(t('incident.create.messages.claimReceiveAfterCommissioning')));
    }
    return Promise.resolve();
  };

  const handleEditFinish = async (values: IncidentEditFormValues) => {
    if (!incident) return;
    const productFormSchema = incident.product_form_schema_snapshot as IncidentFormSchema | null | undefined;
    const payload: IncidentUpdate = {
      title: values.title?.trim(),
      severity: values.severity,
      trouble_date: formatDate(values.trouble_date) || incident.trouble_date,
      claim_receive_date: formatDate(values.claim_receive_date) || incident.claim_receive_date,
      commissioning_date: formatDate(values.commissioning_date),
      production_factory_setting_date: formatDate(values.production_factory_setting_date),
      info_classification: values.info_classification?.trim(),
      claim_class: values.claim_class?.trim(),
      related_gib_no: normalizeOptionalText(values.related_gib_no),
      site_resolution_status: normalizeOptionalText(values.site_resolution_status),
      initial_comment: values.initial_comment?.trim(),
      product_classification: values.product_classification?.trim(),
      defect_location: normalizeOptionalText(values.defect_location),
      installation_environment: values.installation_environment?.trim(),
      model_name: values.model_name?.trim(),
      model_number: normalizePatchText(values.model_number),
      serial_no: normalizePatchText(values.serial_no),
      lot_number: normalizePatchText(values.lot_number),
      is_new_product: values.is_new_product ?? null,
      unit_count: values.unit_count ?? null,
      production_factory: normalizeOptionalText(values.production_factory),
      season: normalizeOptionalText(values.season),
      piping_length: normalizeOptionalText(values.piping_length),
      error_code: normalizeOptionalText(values.error_code),
      room_size: normalizeOptionalText(values.room_size),
      dealer: normalizeOptionalText(values.dealer),
      delivery_destination: normalizeOptionalText(values.delivery_destination),
      installer_name: normalizeOptionalText(values.installer_name),
      delivery_route: normalizeOptionalText(values.delivery_route),
      service_control_no: normalizeOptionalText(values.service_control_no),
      customer_impact: normalizeOptionalText(values.customer_impact),
      phenomenon: values.phenomenon?.trim(),
      temporary_action: normalizePatchText(values.temporary_action),
      product_form_response: normalizeProductFormResponse(productFormSchema, values.product_form_response),
      defect_tag_ids: [...(values.defect_location_tag_ids || []), ...(values.defect_phenomenon_tag_ids || [])],
    };

    setEditSaving(true);
    try {
      await onUpdateIncident(incident.id, payload);
      const fieldChangeItems = await onLoadFieldChanges(incident.id);
      setFieldChanges(fieldChangeItems);
      setEditOpen(false);
    } finally {
      setEditSaving(false);
    }
  };

  const handleTaskStatusChange = (taskId: string, status: IncidentTaskStatus) => {
    handleTaskFieldChange(taskId, {
      status,
      hold_reason: status === 'ON_HOLD' ? undefined : null,
      requested_to_role: status === 'ON_HOLD' ? undefined : null,
      requested_action: status === 'ON_HOLD' ? undefined : null,
      final_response_due_date: status === 'ON_HOLD' ? null : undefined,
    });
  };

  const handleTaskFieldChange = (taskId: string, patch: Partial<IncidentTask>) => {
    setTasks((current) => current.map((task) => (task.id === taskId ? { ...task, ...patch } : task)));
  };

  const handleTaskResponseDraftChange = (taskId: string, value: string) => {
    setTaskResponseDrafts((current) => ({ ...current, [taskId]: value }));
  };

  const handlePartsReturnDraftChange = (taskId: string, patch: PartsReturnDraft) => {
    setPartsReturnDrafts((current) => ({
      ...current,
      [taskId]: {
        ...(current[taskId] || {}),
        ...patch,
      },
    }));
  };

  const handleTaskSave = async (task: IncidentTask) => {
    if (!incident) return;
    if (task.status === 'ON_HOLD' && (!task.hold_reason || !task.requested_to_role || !task.requested_action?.trim())) {
      messageApi.warning(t('incident.detail.tasks.onHoldRequired'));
      return;
    }
    if (
      task.status === 'ON_HOLD'
      && task.hold_reason !== 'AWAITING_PART_RETURN'
      && task.requested_to_role
      && task.hold_reason
      && roleByHoldReason[task.hold_reason] !== task.requested_to_role
    ) {
      messageApi.warning(t('incident.detail.tasks.onHoldTargetMismatch'));
      return;
    }
    if (task.status === 'ON_HOLD' && task.hold_reason === 'AWAITING_PART_RETURN') {
      const draft = partsReturnDrafts[task.id] || {};
      if (!draft.part_name?.trim() || !draft.quantity || !draft.return_reason?.trim() || !draft.ship_to?.trim()) {
        messageApi.warning(t('incident.detail.requests.partsReturnRequired'));
        return;
      }
      setTaskSavingId(task.id);
      try {
        await onCreateRequest(incident.id, {
          source_task_id: task.id,
          target_role: task.requested_to_role as UserRole,
          request_type: 'PARTS_RETURN',
          title: t('incident.detail.requests.partsReturnTitle'),
          requested_action: task.requested_action?.trim() || '',
          due_date: null,
          hold_reason: task.hold_reason,
          part_name: draft.part_name,
          part_number: normalizeOptionalText(draft.part_number),
          quantity: draft.quantity,
          return_reason: draft.return_reason,
          ship_to: draft.ship_to,
          shipping_instruction: normalizeOptionalText(draft.shipping_instruction),
        });
        await refreshTaskWork();
      } catch {
        messageApi.warning(t('incident.detail.requests.createFailed'));
      } finally {
        setTaskSavingId(null);
      }
      return;
    }
    if (task.status === 'RESOLVED' && !task.response_summary?.trim()) {
      messageApi.warning(t('incident.detail.tasks.responseSummaryRequired'));
      return;
    }
    if (task.status === 'CLOSED' && isIssuingPartyConfirmationTask(task) && !taskResponseDrafts[task.id]?.trim()) {
      messageApi.warning(t('incident.detail.tasks.confirmationCloseCommentRequired'));
      return;
    }
    setTaskSavingId(task.id);
    try {
      const responseDraft = normalizeOptionalText(taskResponseDrafts[task.id]);
      const payload: IncidentTaskUpdate = {
        status: task.status,
        action_taken: normalizeOptionalText(task.action_taken),
        root_cause: normalizeOptionalText(task.root_cause),
        hold_reason: task.status === 'ON_HOLD' ? task.hold_reason || null : null,
        requested_to_role: task.status === 'ON_HOLD' ? task.requested_to_role || null : null,
        requested_action: task.status === 'ON_HOLD' ? normalizeOptionalText(task.requested_action) : null,
        final_response_due_date: task.status === 'ON_HOLD' ? null : task.final_response_due_date || null,
      };
      if (task.status === 'IN_PROGRESS' || (task.status === 'CLOSED' && isIssuingPartyConfirmationTask(task))) {
        if (responseDraft) payload.response_summary = responseDraft;
      } else {
        payload.response_summary = normalizeOptionalText(task.response_summary);
      }
      const updated = await onUpdateTask(incident.id, task.id, payload);
      setTasks((current) => current.map((item) => (item.id === task.id ? updated : item)));
      const [taskItems, requestItems, activityItems, timeline] = await Promise.all([
        onLoadTasks(incident.id),
        onLoadRequests(incident.id),
        onLoadTaskActivities(incident.id),
        onLoadProcessTimeline(incident.id),
      ]);
      setTasks(taskItems);
      setRequests(requestItems);
      setTaskActivities(activityItems);
      setProcessTimeline(timeline);
      setTaskResponseDrafts((current) => {
        const next = { ...current };
        delete next[task.id];
        return next;
      });
    } finally {
      setTaskSavingId(null);
    }
  };

  const refreshTaskWork = async () => {
    if (!incident) return;
    const [taskItems, requestItems, activityItems, timeline] = await Promise.all([
      onLoadTasks(incident.id),
      onLoadRequests(incident.id),
      onLoadTaskActivities(incident.id),
      onLoadProcessTimeline(incident.id),
    ]);
    setTasks(taskItems);
    setRequests(requestItems);
    setTaskActivities(activityItems);
    setProcessTimeline(timeline);
  };

  const handleUpdateRequestStatus = async (
    request: IncidentRequest,
    status: IncidentRequestStatus,
    body?: string
  ) => {
    if (!incident) return;
    if (status === 'RESOLVED' && !body?.trim()) {
      messageApi.warning(t('incident.detail.requests.responseRequired'));
      return;
    }
    setRequestActionId(request.id);
    try {
      await onUpdateRequest(incident.id, request.id, { status, body: normalizeOptionalText(body) });
      setRequestDrafts((current) => ({ ...current, [request.id]: '' }));
      await refreshTaskWork();
    } finally {
      setRequestActionId(null);
    }
  };

  const getOutgoingRequestsForTask = (task: IncidentTask) =>
    requests.filter((request) => request.source_task_id === task.id && request.status !== 'CANCELLED');

  const getActiveOutgoingRequestsForTask = (task: IncidentTask) =>
    getOutgoingRequestsForTask(task).filter((request) => request.status !== 'CLOSED');

  const hasPendingRequestReview = (task: IncidentTask) =>
    getActiveOutgoingRequestsForTask(task).some((request) => request.status === 'RESOLVED');

  const isPrimaryResponseTask = (task: IncidentTask) =>
    primaryResponseTaskTypes.includes(task.task_type) && !task.requested_by_task_id;

  const isIssuingPartyConfirmationTask = (task: IncidentTask) =>
    task.task_type === issuingPartyConfirmationTaskType && !task.requested_by_task_id;

  const getIssuingPartyResolution = () => {
    const confirmationTask = tasks.find((task) => isIssuingPartyConfirmationTask(task) && task.status === 'CLOSED');
    if (!confirmationTask) return null;
    const activities = taskActivities.filter((activity) => activity.task_id === confirmationTask.id);
    const closeActivity = activities.find(
      (activity) => activity.activity_type === 'STATUS_CHANGED' && activity.to_status === 'CLOSED'
    );
    const commentActivity = activities.find((activity) => activity.activity_group_id === closeActivity?.activity_group_id && activity.body);
    return {
      task: confirmationTask,
      comment: confirmationTask.response_summary || commentActivity?.body || null,
      actorName: closeActivity?.actor_display_name || commentActivity?.actor_display_name || null,
      resolvedAt: closeActivity?.created_at || confirmationTask.closed_at || confirmationTask.updated_at,
    };
  };

  const hasOpenIssuerRequestForTask = (task: IncidentTask) =>
    requests.some(
      (request) =>
        request.source_role === 'ISSUING_PARTY' &&
        request.status !== 'CLOSED' &&
        request.status !== 'CANCELLED' &&
        (request.target_task_id ? request.target_task_id === task.id : request.target_role === task.responsible_role)
    );

  const getSourceRequestClassName = (request: IncidentRequest) =>
    [
      'sourceRequestSummary',
      request.status === 'RESOLVED' ? 'sourceRequestSummaryResolved' : null,
      request.status === 'CLOSED' ? 'sourceRequestSummaryClosed' : null,
      request.status !== 'RESOLVED' && request.status !== 'CLOSED' ? 'sourceRequestSummaryActive' : null,
    ]
      .filter(Boolean)
      .join(' ');

  const getLatestResponseActivity = (request: IncidentRequest) => {
    const activities = [...request.activities].reverse();
    return (
      activities.find(
        (activity) => activity.activity_type === 'STATUS_CHANGED' && activity.to_status === 'RESOLVED' && activity.body
      ) || activities.find((activity) => activity.activity_type === 'RESPONSE_ADDED' && activity.body)
    );
  };

  const getReviewedRequestForActivity = (activity: TaskActivity) => {
    if (activity.activity_type !== 'REQUEST_REVIEWED') return null;
    const candidates = requests.filter(
      (request) =>
        request.status === 'CLOSED' &&
        request.source_task_id === activity.task_id &&
        (activity.related_task_id ? request.target_task_id === activity.related_task_id : true)
    );
    if (candidates.length === 0) return null;
    return [...candidates].sort((a, b) => {
      const activityTime = new Date(activity.created_at).getTime();
      const aTime = new Date(a.closed_at || a.updated_at).getTime();
      const bTime = new Date(b.closed_at || b.updated_at).getTime();
      return Math.abs(aTime - activityTime) - Math.abs(bTime - activityTime);
    })[0];
  };

  const renderRequestActivityItem = (request: IncidentRequest, activity: IncidentRequestActivity) => (
    <div className="sourceRequestActivity" key={activity.id}>
      <div className="sourceRequestActivityHeader">
        <Space wrap>
          <Text strong>{t(`incident.requestActivity.${activity.activity_type}`)}</Text>
          {activity.edit_count > 0 && (
            <Button size="small" type="link" onClick={() => openAuditHistory({ type: 'requestActivity', request, activity })}>
              {t('incident.detail.audit.edited')}
            </Button>
          )}
        </Space>
        <Text type="secondary">
          {[
            activity.actor_display_name,
            activity.actor_role ? t(`incident.role.${activity.actor_role}`) : null,
            new Date(activity.created_at).toLocaleString(),
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
      </div>
      {activity.body && (
        <Paragraph className="taskActivityBody">
          {renderLocalizedText('incident_request_activity', activity.id, 'body', activity.body)}
        </Paragraph>
      )}
    </div>
  );

  const renderClosedSourceRequestSnapshot = (request: IncidentRequest, reviewComment?: string | null, reviewActivityId?: string) => {
    const latestResponse = getLatestResponseActivity(request);
    const statusActivities = request.activities.filter((activity) => activity.activity_type !== 'REQUEST_CREATED');
    return (
      <div className="requestReviewedSnapshot">
        <Text type="secondary">
          {[
            `${t('incident.detail.requests.to')}: ${t(`incident.role.${request.target_role}`)}`,
            t(`incident.requestType.${request.request_type}`),
            t(`incident.taskStatus.CLOSED`),
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        <Text type="secondary">
          {[
            `${t('incident.detail.requests.reviewer')}: ${request.activities.find((activity) => activity.activity_type === 'CONFIRMED_CLOSED')?.actor_display_name || '-'}`,
            `${t('incident.detail.requests.reviewedAt')}: ${
              request.closed_at ? new Date(request.closed_at).toLocaleString() : new Date(request.updated_at).toLocaleString()
            }`,
          ].join(' · ')}
        </Text>
        {request.title && (
          <Text strong>{requestDisplayTitle(request)}</Text>
        )}
        <div className="requestReviewedSection">
          <Text strong>{t('incident.detail.audit.requestContent')}</Text>
          <Paragraph className="taskActivityBody">
            {renderLocalizedText('incident_request', request.id, 'requested_action', request.requested_action)}
          </Paragraph>
        </div>
        {latestResponse && (
          <div className="requestReviewedSection">
            <Text strong>{t('incident.detail.requests.finalResponse')}</Text>
            <Paragraph className="taskActivityBody">
              {renderLocalizedText('incident_request_activity', latestResponse.id, 'body', latestResponse.body)}
            </Paragraph>
          </div>
        )}
        {reviewComment && (
          <div className="requestReviewedSection">
            <Text strong>{t('incident.detail.requests.reviewComment')}</Text>
            <Paragraph className="taskActivityBody">
              {reviewActivityId
                ? renderLocalizedText('task_activity', reviewActivityId, 'body', reviewComment)
                : reviewComment}
            </Paragraph>
          </div>
        )}
        {statusActivities.length > 0 && (
          <details className="sourceRequestHistoryDetails">
            <summary>{t('incident.detail.requests.showHistory')}</summary>
            <div className="sourceRequestActivityList">
              {statusActivities.map((activity) => renderRequestActivityItem(request, activity))}
            </div>
          </details>
        )}
      </div>
    );
  };

  const renderSourceRequestActions = (request: IncidentRequest) => {
    if (!canCloseRequest(request)) return null;
    const draft = requestDrafts[request.id] || '';
    return (
      <div className="sourceRequestActions">
        <Input
          value={draft}
          onChange={(event) => setRequestDrafts((current) => ({ ...current, [request.id]: event.target.value }))}
          placeholder={t('incident.detail.requests.sourceActionPlaceholder')}
        />
        <Space wrap>
          <Button loading={requestActionId === request.id} onClick={() => handleUpdateRequestStatus(request, 'IN_PROGRESS', draft)}>
            {t('incident.detail.requests.reopen')}
          </Button>
          <Button loading={requestActionId === request.id} type="primary" onClick={() => handleUpdateRequestStatus(request, 'CLOSED', draft)}>
            {t('incident.detail.requests.confirmClose')}
          </Button>
        </Space>
      </div>
    );
  };

  const renderSourceRequestSummary = (task: IncidentTask) => {
    const sourceRequests = getActiveOutgoingRequestsForTask(task);
    if (sourceRequests.length === 0) return null;
    return (
      <div className="sourceRequestSummaryList">
        {sourceRequests.map((request) => {
          const latestResponse = getLatestResponseActivity(request);
          const statusActivities = request.activities.filter((activity) => activity.activity_type !== 'REQUEST_CREATED');
          return (
            <div className={getSourceRequestClassName(request)} key={request.id}>
              <div className="sourceRequestHeader">
                <Space wrap>
                  <Tag color="geekblue">{t(`incident.requestType.${request.request_type}`)}</Tag>
                  <IncidentTaskStatusTag status={request.status === 'CANCELLED' ? 'CLOSED' : request.status} />
                  {request.edit_count > 0 && (
                    <Button size="small" type="link" onClick={() => openAuditHistory({ type: 'request', request })}>
                      {t('incident.detail.audit.edited')}
                    </Button>
                  )}
                </Space>
              </div>
              <Text type="secondary">
                {[
                  `${t('incident.detail.requests.to')}: ${t(`incident.role.${request.target_role}`)}`,
                  request.due_date ? `${t('incident.detail.requests.dueDate')}: ${request.due_date}` : null,
                  new Date(request.created_at).toLocaleString(),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
              {request.title && (
                <Text strong>{requestDisplayTitle(request)}</Text>
              )}
              <Paragraph className="taskActivityBody">
                {renderLocalizedText('incident_request', request.id, 'requested_action', request.requested_action)}
              </Paragraph>
              {['RESOLVED', 'CLOSED'].includes(request.status) && latestResponse ? (
                <>
                  <div className="sourceRequestFinalAnswer">
                    <Text strong>{t('incident.detail.requests.finalResponse')}</Text>
                    <Paragraph className="taskActivityBody">
                      {renderLocalizedText('incident_request_activity', latestResponse.id, 'body', latestResponse.body)}
                    </Paragraph>
                  </div>
                  {statusActivities.length > 0 && (
                    <details className="sourceRequestHistoryDetails">
                      <summary>{t('incident.detail.requests.showHistory')}</summary>
                      <div className="sourceRequestActivityList">
                        {statusActivities.map((activity) => renderRequestActivityItem(request, activity))}
                      </div>
                    </details>
                  )}
                </>
              ) : (
                statusActivities.length > 0 && (
                  <div className="sourceRequestActivityList">
                    {statusActivities.map((activity) => renderRequestActivityItem(request, activity))}
                  </div>
                )
              )}
              {renderSourceRequestActions(request)}
            </div>
          );
        })}
      </div>
    );
  };

  const renderOnHoldTaskSummary = (task: IncidentTask) => (
    <div className="taskHoldSummary">
      <Text strong>
        {hasPendingRequestReview(task)
          ? t('incident.detail.requests.pendingReviewTitle')
          : t('incident.detail.requests.sourceStatusTitle')}
      </Text>
      {renderSourceRequestSummary(task)}
    </div>
  );

  const getAuditTargetTitle = (target: AuditEditTarget | null) => {
    if (!target) return '';
    if (target.type === 'taskActivity') return formatTaskActivityTitle(target.activity);
    if (target.type === 'request') return t('incident.detail.audit.requestContent');
    return t(`incident.requestActivity.${target.activity.activity_type}`);
  };

  const openAuditEditor = (target: AuditEditTarget) => {
    setAuditEditTarget(target);
    setAuditEditReason('');
    if (target.type === 'taskActivity') {
      const values: Record<string, string> = {};
      getEditableTaskActivityFields(target.activity).forEach((field) => {
        const value = target.activity[field as keyof TaskActivity];
        values[field] = typeof value === 'string' ? value : '';
      });
      setAuditEditValues(values);
      return;
    }
    if (target.type === 'request') {
      setAuditEditValues({
        title: target.request.title || '',
        requested_action: target.request.requested_action || '',
        part_name: target.request.part_name || '',
        part_number: target.request.part_number || '',
        quantity: target.request.quantity?.toString() || '',
        return_reason: target.request.return_reason || '',
        ship_to: target.request.ship_to || '',
        shipping_instruction: target.request.shipping_instruction || '',
        tracking_number: target.request.tracking_number || '',
        shipped_at: target.request.shipped_at || '',
        received_at: target.request.received_at || '',
      });
      return;
    }
    setAuditEditValues({ body: target.activity.body || '' });
  };

  const closeAuditEditor = () => {
    setAuditEditTarget(null);
    setAuditEditValues({});
    setAuditEditReason('');
  };

  const handleSaveAuditEdit = async () => {
    if (!incident || !auditEditTarget) return;
    const reason = auditEditReason.trim();
    if (!reason) {
      messageApi.warning(t('incident.detail.audit.reasonRequired'));
      return;
    }
    setAuditEditSaving(true);
    try {
      if (auditEditTarget.type === 'taskActivity') {
        const payload: TaskActivityUpdate = { edit_reason: reason };
        getEditableTaskActivityFields(auditEditTarget.activity).forEach((field) => {
          payload[field] = auditEditValues[field] || null;
        });
        await onUpdateTaskActivity(incident.id, auditEditTarget.activity.id, {
          ...payload,
        });
        const [taskItems, requestItems, activityItems, timeline] = await Promise.all([
          onLoadTasks(incident.id),
          onLoadRequests(incident.id),
          onLoadTaskActivities(incident.id),
          onLoadProcessTimeline(incident.id),
        ]);
        setTasks(taskItems);
        setRequests(requestItems);
        setTaskActivities(activityItems);
        setProcessTimeline(timeline);
      } else if (auditEditTarget.type === 'request') {
        await onUpdateRequest(incident.id, auditEditTarget.request.id, {
          title: auditEditValues.title || null,
          requested_action: auditEditValues.requested_action || auditEditTarget.request.requested_action,
          part_name: auditEditValues.part_name || null,
          part_number: auditEditValues.part_number || null,
          quantity: auditEditValues.quantity ? Number(auditEditValues.quantity) : null,
          return_reason: auditEditValues.return_reason || null,
          ship_to: auditEditValues.ship_to || null,
          shipping_instruction: auditEditValues.shipping_instruction || null,
          tracking_number: auditEditValues.tracking_number || null,
          shipped_at: auditEditValues.shipped_at || null,
          received_at: auditEditValues.received_at || null,
          edit_reason: reason,
        });
        const requestItems = await onLoadRequests(incident.id);
        setRequests(requestItems);
      } else {
        await onUpdateRequestActivity(incident.id, auditEditTarget.request.id, auditEditTarget.activity.id, {
          body: auditEditValues.body || '',
          edit_reason: reason,
        });
        const requestItems = await onLoadRequests(incident.id);
        setRequests(requestItems);
      }
      closeAuditEditor();
    } finally {
      setAuditEditSaving(false);
    }
  };

  const openAuditHistory = async (target: AuditEditTarget) => {
    if (!incident) return;
    setAuditHistoryTarget(target);
    setAuditHistory([]);
    setAuditHistoryLoading(true);
    try {
      const edits =
        target.type === 'taskActivity'
          ? await onLoadTaskActivityEdits(incident.id, target.activity.id)
          : target.type === 'request'
            ? await onLoadRequestEdits(incident.id, target.request.id)
            : await onLoadRequestActivityEdits(incident.id, target.request.id, target.activity.id);
      setAuditHistory(edits);
    } finally {
      setAuditHistoryLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!incident) return;
    const body = commentBody.trim();
    if (!body) {
      messageApi.warning(t('incident.detail.comments.bodyRequired'));
      return;
    }
    setCommentSubmitting(true);
    try {
      const comment = await onAddComment(incident.id, body);
      setComments((current) => [...current, comment]);
      setCommentBody('');
    } finally {
      setCommentSubmitting(false);
    }
  };

  const startEditComment = (comment: IncidentComment) => {
    setEditingCommentId(comment.id);
    setEditingCommentBody(comment.body);
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentBody('');
  };

  const handleUpdateComment = async (commentId: string) => {
    if (!incident) return;
    const body = editingCommentBody.trim();
    if (!body) {
      messageApi.warning(t('incident.detail.comments.bodyRequired'));
      return;
    }
    setCommentActionId(commentId);
    try {
      const updated = await onUpdateComment(incident.id, commentId, body);
      setComments((current) => current.map((comment) => (comment.id === commentId ? updated : comment)));
      cancelEditComment();
    } finally {
      setCommentActionId(null);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    if (!incident) return;
    Modal.confirm({
      title: t('incident.detail.commentDelete.title'),
      content: t('incident.detail.commentDelete.description'),
      okText: t('incident.detail.commentDelete.ok'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        setCommentActionId(commentId);
        try {
          await onDeleteComment(incident.id, commentId);
          setComments((current) => current.filter((comment) => comment.id !== commentId));
        } finally {
          setCommentActionId(null);
        }
      },
    });
  };

  const handleAddTaskInternalComment = async (task: IncidentTask) => {
    if (!incident) return;
    const body = (taskInternalCommentBodies[task.id] || '').trim();
    if (!body) {
      messageApi.warning(t('incident.detail.comments.bodyRequired'));
      return;
    }
    setTaskInternalCommentSubmittingId(task.id);
    try {
      const comment = await onAddTaskInternalComment(incident.id, task.id, body);
      setTaskInternalComments((current) => ({
        ...current,
        [task.id]: [...(current[task.id] || []), comment],
      }));
      setTaskInternalCommentBodies((current) => ({ ...current, [task.id]: '' }));
    } finally {
      setTaskInternalCommentSubmittingId(null);
    }
  };

  const startEditTaskInternalComment = (comment: IncidentTaskInternalComment) => {
    setEditingTaskInternalCommentId(comment.id);
    setEditingTaskInternalCommentBody(comment.body);
  };

  const cancelEditTaskInternalComment = () => {
    setEditingTaskInternalCommentId(null);
    setEditingTaskInternalCommentBody('');
  };

  const handleUpdateTaskInternalComment = async (task: IncidentTask, commentId: string) => {
    if (!incident) return;
    const body = editingTaskInternalCommentBody.trim();
    if (!body) {
      messageApi.warning(t('incident.detail.comments.bodyRequired'));
      return;
    }
    setTaskInternalCommentActionId(commentId);
    try {
      const updated = await onUpdateTaskInternalComment(incident.id, task.id, commentId, body);
      setTaskInternalComments((current) => ({
        ...current,
        [task.id]: (current[task.id] || []).map((comment) => (comment.id === commentId ? updated : comment)),
      }));
      cancelEditTaskInternalComment();
    } finally {
      setTaskInternalCommentActionId(null);
    }
  };

  const handleDeleteTaskInternalComment = (task: IncidentTask, commentId: string) => {
    if (!incident) return;
    Modal.confirm({
      title: t('incident.detail.commentDelete.title'),
      content: t('incident.detail.commentDelete.description'),
      okText: t('incident.detail.commentDelete.ok'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        setTaskInternalCommentActionId(commentId);
        try {
          await onDeleteTaskInternalComment(incident.id, task.id, commentId);
          setTaskInternalComments((current) => ({
            ...current,
            [task.id]: (current[task.id] || []).filter((comment) => comment.id !== commentId),
          }));
        } finally {
          setTaskInternalCommentActionId(null);
        }
      },
    });
  };

  const handleCloseIncident = async () => {
    if (!incident) return;
    const comment = closeComment.trim();
    if (!comment) {
      messageApi.warning(t('incident.detail.close.commentRequired'));
      return;
    }

    setCloseSaving(true);
    try {
      await onCloseIncident(incident.id, comment);
      const commentItems = await onLoadComments(incident.id);
      setComments(commentItems);
      setCloseComment('');
      setCloseOpen(false);
    } finally {
      setCloseSaving(false);
    }
  };

  const formatTaskActivityTitle = (activity: TaskActivity) => {
    if (activity.activity_type === 'STATUS_CHANGED') {
      return `${t('incident.taskActivity.STATUS_CHANGED')}: ${activity.from_status ? t(`incident.taskStatus.${activity.from_status}`) : '-'} -> ${
        activity.to_status ? t(`incident.taskStatus.${activity.to_status}`) : '-'
      }`;
    }
    if (activity.activity_type === 'DUE_DATE_UPDATED') {
      return `${t('incident.taskActivity.DUE_DATE_UPDATED')}: ${activity.due_date || '-'}`;
    }
    if (activity.activity_type === 'ON_HOLD_REQUESTED') {
      return `${t('incident.taskActivity.ON_HOLD_REQUESTED')}: ${
        activity.hold_reason ? t(`incident.holdReason.${activity.hold_reason}`) : '-'
      }`;
    }
    return t(`incident.taskActivity.${activity.activity_type}`);
  };

  const renderTaskActivityDetails = (activity: TaskActivity, compact = false) => {
    const reviewedRequest = getReviewedRequestForActivity(activity);
    if (reviewedRequest) {
      return (
        <>
          {compact && <Text type="secondary">{formatTaskActivityTitle(activity)}</Text>}
          {renderClosedSourceRequestSnapshot(reviewedRequest, activity.body, activity.id)}
        </>
      );
    }
    return (
      <>
        {compact && <Text type="secondary">{formatTaskActivityTitle(activity)}</Text>}
        {activity.related_task_id && (
          <Text type="secondary">
            {`${t('incident.detail.tasks.relatedTask')}: ${
              tasks.find((item) => item.id === activity.related_task_id)
                ? t(`incident.taskType.${tasks.find((item) => item.id === activity.related_task_id)?.task_type}`)
              : activity.related_task_id
            }`}
          </Text>
        )}
        {activity.requested_to_role && (
          <Text type="secondary">{`${t('incident.detail.tasks.requestedToRole')}: ${t(`incident.role.${activity.requested_to_role}`)}`}</Text>
        )}
        {activity.requested_action && (
          <Paragraph className="taskActivityBody">
            {renderLocalizedText('task_activity', activity.id, 'requested_action', activity.requested_action)}
          </Paragraph>
        )}
        {activity.body && (
          <Paragraph className="taskActivityBody">
            {renderLocalizedText('task_activity', activity.id, 'body', activity.body)}
          </Paragraph>
        )}
        {activity.root_cause && (
          <Text type="secondary">
            {`${t('incident.detail.tasks.rootCause')}: ${localizedPlainText('task_activity', activity.id, 'root_cause', activity.root_cause)}`}
          </Text>
        )}
      </>
    );
  };

  const renderTaskTimeline = (task: IncidentTask) => {
    const items = taskActivities.filter((activity) => activity.task_id === task.id);
    if (items.length === 0) return null;
    const groups = items.reduce<Array<{ id: string; activities: TaskActivity[] }>>((acc, activity) => {
      const groupId = activity.activity_group_id || activity.id;
      const existing = acc.find((group) => group.id === groupId);
      if (existing) {
        existing.activities.push(activity);
      } else {
        acc.push({ id: groupId, activities: [activity] });
      }
      return acc;
    }, []);
    const latestDeletableGroup = groups.find((group) =>
      group.activities.some((activity) => deletableActivityTypes.includes(activity.activity_type))
    );
    const getPrimaryActivity = (activities: TaskActivity[]) =>
      activities.find((activity) => deletableActivityTypes.includes(activity.activity_type)) || activities[0];
    const isRequesterReceivedAnswer = (activity: TaskActivity) =>
      ['REQUEST_ANSWERED', 'REQUEST_REVIEWED'].includes(activity.activity_type) &&
      activity.related_task_id !== null &&
      activity.related_task_id !== undefined;
    const canDeleteGroup = (primaryActivity: TaskActivity, groupId: string) =>
      task.status !== 'CLOSED' &&
      latestDeletableGroup?.id === groupId &&
      !isRequesterReceivedAnswer(primaryActivity) &&
      deletableActivityTypes.includes(primaryActivity.activity_type) &&
      (primaryActivity.actor_user_id === currentUser?.id || userAssignments.some((assignment) => adminRoles.includes(assignment.role)));
  const handleDeleteActivity = (activity: TaskActivity) => {
      if (!incident) return;
      Modal.confirm({
        title: t('incident.detail.activityDelete.title'),
        content: t('incident.detail.activityDelete.description'),
        okText: t('incident.detail.activityDelete.ok'),
        cancelText: t('incident.create.actions.cancel'),
        okButtonProps: { danger: true },
        onOk: async () => {
          setActivityDeletingId(activity.id);
          try {
            await onDeleteTaskActivity(incident.id, activity.id);
            const [taskItems, activityItems, timeline] = await Promise.all([
              onLoadTasks(incident.id),
              onLoadTaskActivities(incident.id),
              onLoadProcessTimeline(incident.id),
            ]);
            setTasks(taskItems);
            setTaskActivities(activityItems);
            setProcessTimeline(timeline);
          } catch {
            messageApi.error(t('incident.detail.activityDelete.failed'));
          } finally {
            setActivityDeletingId(null);
          }
        },
      });
    };
    return (
      <div className="taskActivityList">
        {groups.map((group) => {
          const primaryActivity = getPrimaryActivity(group.activities);
          const detailActivities = group.activities.filter((activity) => activity.id !== primaryActivity.id);
          const reviewedRequest = getReviewedRequestForActivity(primaryActivity);
          if (reviewedRequest) {
            return (
              <div className="taskActivityCard taskActivityCardRequestReviewed" id={`task-activity-${primaryActivity.id}`} key={group.id}>
                <div className="taskActivityHeader">
                  <Space className="taskActivityHeaderTitle" wrap size={[8, 4]}>
                    <Text strong>{t('incident.detail.requests.answeredTitle')}</Text>
                  </Space>
                </div>
                {renderClosedSourceRequestSnapshot(reviewedRequest, primaryActivity.body, primaryActivity.id)}
                {detailActivities.length > 0 && (
                  <div className="taskActivitySubList">
                    {detailActivities.map((activity) => (
                      <div className="taskActivitySubItem" key={activity.id}>
                        {renderTaskActivityDetails(activity, true)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          }
          const cardClassName = [
            'taskActivityCard',
            primaryActivity.activity_type === 'REQUEST_ANSWERED' ? 'taskActivityCardRequestAnswer' : null,
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <div className={cardClassName} id={`task-activity-${primaryActivity.id}`} key={group.id}>
              <div className="taskActivityHeader">
                <Space className="taskActivityHeaderTitle" wrap size={[8, 4]}>
                  <Text strong>{formatTaskActivityTitle(primaryActivity)}</Text>
                  {primaryActivity.edit_count > 0 && (
                    <Button size="small" type="link" onClick={() => openAuditHistory({ type: 'taskActivity', activity: primaryActivity })}>
                      {t('incident.detail.audit.edited')}
                    </Button>
                  )}
                </Space>
                <Space className="taskActivityHeaderActions" wrap size={[8, 4]}>
                  {canDeleteGroup(primaryActivity, group.id) && (
                    <Button
                      danger
                      loading={activityDeletingId === primaryActivity.id}
                      size="small"
                      onClick={() => handleDeleteActivity(primaryActivity)}
                    >
                      {t('incident.detail.actions.delete')}
                    </Button>
                  )}
                  {hasEditableTaskActivityContent(primaryActivity) && canManageAuditItem(primaryActivity.actor_user_id) && (
                    <Button size="small" onClick={() => openAuditEditor({ type: 'taskActivity', activity: primaryActivity })}>
                      {t('incident.detail.actions.edit')}
                    </Button>
                  )}
                </Space>
              </div>
              <Text type="secondary">
                {[
                  primaryActivity.actor_display_name,
                  primaryActivity.actor_role ? t(`incident.role.${primaryActivity.actor_role}`) : null,
                  new Date(primaryActivity.created_at).toLocaleString(),
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
              {renderTaskActivityDetails(primaryActivity)}
              {detailActivities.length > 0 && (
                <div className="taskActivitySubList">
                  {detailActivities.map((activity) => (
                    <div className="taskActivitySubItem" key={activity.id}>
                      {renderTaskActivityDetails(activity, true)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFieldChanges = () => {
    if (fieldChanges.length === 0) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('incident.detail.fieldChanges.empty')} />;
    }
    return (
      <Timeline
        items={fieldChanges.map((change) => ({
          children: (
            <Space direction="vertical" size={0}>
              <Text>{t(`incident.field.${change.field_name}`, { defaultValue: change.field_name })}</Text>
              <Text type="secondary">
                {[change.changed_by_display_name, new Date(change.created_at).toLocaleString()].filter(Boolean).join(' · ')}
              </Text>
              <Text type="secondary">
                {`${change.old_value || '-'} -> ${change.new_value || '-'}`}
              </Text>
            </Space>
          ),
        }))}
      />
    );
  };

  const handleDeleteAttachment = (attachment: IncidentAttachment) => {
    if (!incident) return;
    Modal.confirm({
      title: t('incident.detail.attachmentDelete.title'),
      content: t('incident.detail.attachmentDelete.description'),
      okText: t('incident.detail.attachmentDelete.ok'),
      cancelText: t('incident.create.actions.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        setAttachmentDeletingId(attachment.id);
        try {
          await onDeleteAttachment(incident.id, attachment.id);
          setAttachments((current) => current.filter((item) => item.id !== attachment.id));
        } finally {
          setAttachmentDeletingId(null);
        }
      },
    });
  };

  const canDeleteAttachment = (attachment: IncidentAttachment) =>
    attachment.uploaded_by_user_id === currentUser?.id ||
    userAssignments.some((assignment) => adminRoles.includes(assignment.role));

  const resolveAttachmentUrl = async (attachment: IncidentAttachment): Promise<string> => {
    if (attachment.upload_url) return attachment.upload_url;
    return onCreateAttachmentDownload(attachment.incident_id, attachment.id);
  };

  const handlePreviewAttachment = async (attachment: IncidentAttachment) => {
    setPreviewAttachment(attachment);
    setPreviewUrl(null);
    setPreviewText(null);
    setPreviewLoading(true);
    try {
      const url = await resolveAttachmentUrl(attachment);
      setPreviewUrl(url);
      if (isTextAttachment(attachment)) {
        const response = await fetch(url);
        setPreviewText(await response.text());
      }
    } catch {
      messageApi.error(t('incident.detail.attachments.openFailed'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadAttachment = async (attachment: IncidentAttachment) => {
    try {
      const url = await resolveAttachmentUrl(attachment);
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Attachment download failed: ${response.status}`);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = objectUrl;
      link.download = attachment.file_name;
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      messageApi.error(t('incident.detail.attachments.openFailed'));
    }
  };

  const renderTaskResponseFields = (task: IncidentTask) => {
    const editable = canEditTask(task);
    const issuingPartyResolution = getIssuingPartyResolution();
    if (task.status === 'CLOSED') {
      if (isIssuingPartyConfirmationTask(task) && issuingPartyResolution) {
        return (
          <div className="taskIncidentResolvedMessage">
            <Text strong>{t('incident.detail.tasks.incidentResolvedByIssuerTitle')}</Text>
            {issuingPartyResolution.comment && (
              <Paragraph className="taskActivityBody">
                {renderLocalizedText('incident_task', task.id, 'response_summary', issuingPartyResolution.comment)}
              </Paragraph>
            )}
            <Text type="secondary">
              {[
                `${t('incident.detail.tasks.incidentResolver')}: ${issuingPartyResolution.actorName || '-'}`,
                `${t('incident.detail.tasks.incidentResolvedAt')}: ${new Date(issuingPartyResolution.resolvedAt).toLocaleString()}`,
              ].join(' · ')}
            </Text>
          </div>
        );
      }
      if (isIssuingPartyConfirmationTask(task) && !task.closed_at) {
        return (
          <>
            {renderTaskInputLanguageHint(task)}
            <TextArea
              rows={3}
              disabled={!editable}
              value={taskResponseDrafts[task.id] || ''}
              onChange={(event) => handleTaskResponseDraftChange(task.id, event.target.value)}
              placeholder={t('incident.detail.tasks.confirmationCloseCommentPlaceholder')}
              status={!taskResponseDrafts[task.id]?.trim() ? 'warning' : undefined}
            />
          </>
        );
      }
      return (
        <div className="taskClosedMessage">
          <Text strong>{t('incident.detail.tasks.closedTitle')}</Text>
          <Text type="secondary">{t('incident.detail.tasks.closedDescription')}</Text>
        </div>
      );
    }
    const finalResponseDueDate = (
      <DatePicker
        className="fullWidthControl"
        disabled={!editable}
        value={task.final_response_due_date ? dayjs(task.final_response_due_date) : null}
        onChange={(value) => handleTaskFieldChange(task.id, { final_response_due_date: value ? value.format('YYYY-MM-DD') : null })}
        placeholder={t('incident.detail.tasks.finalResponseDueDate')}
      />
    );
    const sourceRequestSummary = getActiveOutgoingRequestsForTask(task).length > 0 ? renderOnHoldTaskSummary(task) : null;
    if (hasPendingRequestReview(task)) {
      return sourceRequestSummary;
    }

    if (task.status === 'ON_HOLD') {
      if (sourceRequestSummary) {
        return sourceRequestSummary;
      }
      const partsDraft = partsReturnDrafts[task.id] || {};
      const roleOptions = requestableWorkflowRoles.filter((role) => role !== task.responsible_role);
      const holdReasonOptions: TaskHoldReason[] = [...roleOptions.map((role) => holdReasonByRole[role]), 'AWAITING_PART_RETURN'];
      const requestedToRoleValue = roleOptions.find((role) => role === task.requested_to_role);
      return (
        <>
          <div className="taskHoldGrid">
            <Select
              disabled={!editable}
              value={task.hold_reason || undefined}
              onChange={(value: TaskHoldReason) => handleTaskFieldChange(task.id, {
                hold_reason: value,
                requested_to_role: roleByHoldReason[value] || task.requested_to_role,
              })}
              placeholder={t('incident.detail.tasks.holdReason')}
              status={!task.hold_reason ? 'warning' : undefined}
              options={holdReasonOptions.map((reason) => ({
                value: reason,
                label: t(`incident.holdReason.${reason}`),
              }))}
            />
            <Select
              disabled={!editable}
              value={requestedToRoleValue}
              onChange={(value: WorkflowRole) => handleTaskFieldChange(task.id, {
                requested_to_role: value,
                hold_reason: task.hold_reason === 'AWAITING_PART_RETURN' ? task.hold_reason : holdReasonByRole[value],
              })}
              placeholder={t('incident.detail.tasks.requestedToRole')}
              status={!task.requested_to_role ? 'warning' : undefined}
              options={roleOptions.map((role) => ({
                value: role,
                label: t(`incident.role.${role}`),
              }))}
            />
          </div>
          <TextArea
            rows={3}
            disabled={!editable}
            value={task.requested_action || ''}
            onChange={(event) => handleTaskFieldChange(task.id, { requested_action: event.target.value })}
            placeholder={t('incident.detail.tasks.requestedActionRequiredPlaceholder')}
            status={!task.requested_action?.trim() ? 'warning' : undefined}
          />
          {renderTaskInputLanguageHint(task)}
          {task.hold_reason === 'AWAITING_PART_RETURN' && (
            <div className="partsReturnGrid">
              <Input
                disabled={!editable}
                value={partsDraft.part_name || ''}
                onChange={(event) => handlePartsReturnDraftChange(task.id, { part_name: event.target.value })}
                placeholder={t('incident.detail.requests.partName')}
                status={!partsDraft.part_name?.trim() ? 'warning' : undefined}
              />
              <Input
                disabled={!editable}
                value={partsDraft.part_number || ''}
                onChange={(event) => handlePartsReturnDraftChange(task.id, { part_number: event.target.value })}
                placeholder={t('incident.detail.requests.partNumber')}
              />
              <Input
                disabled={!editable}
                type="number"
                min={1}
                value={partsDraft.quantity ?? ''}
                onChange={(event) => handlePartsReturnDraftChange(task.id, { quantity: event.target.value ? Number(event.target.value) : null })}
                placeholder={t('incident.detail.requests.quantity')}
                status={!partsDraft.quantity ? 'warning' : undefined}
              />
              <Input
                disabled={!editable}
                value={partsDraft.ship_to || ''}
                onChange={(event) => handlePartsReturnDraftChange(task.id, { ship_to: event.target.value })}
                placeholder={t('incident.detail.requests.shipTo')}
                status={!partsDraft.ship_to?.trim() ? 'warning' : undefined}
              />
              <TextArea
                rows={2}
                disabled={!editable}
                value={partsDraft.return_reason || ''}
                onChange={(event) => handlePartsReturnDraftChange(task.id, { return_reason: event.target.value })}
                placeholder={t('incident.detail.requests.returnReason')}
                status={!partsDraft.return_reason?.trim() ? 'warning' : undefined}
              />
              <TextArea
                rows={2}
                disabled={!editable}
                value={partsDraft.shipping_instruction || ''}
                onChange={(event) => handlePartsReturnDraftChange(task.id, { shipping_instruction: event.target.value })}
                placeholder={t('incident.detail.requests.shippingInstruction')}
              />
            </div>
          )}
          <Text type="secondary" className="taskRuleText">
            {t('incident.detail.tasks.requestSlaRule')}
          </Text>
        </>
      );
    }

    if (task.status === 'RESOLVED') {
      if (isPrimaryResponseTask(task) && task.resolved_at) {
        if (issuingPartyResolution) {
          return (
            <div className="taskIncidentResolvedMessage">
              <Text strong>{t('incident.detail.tasks.incidentResolvedForResponseTaskTitle')}</Text>
              {issuingPartyResolution.comment && (
                <Paragraph className="taskActivityBody">
                  {renderLocalizedText('incident_task', task.id, 'response_summary', issuingPartyResolution.comment)}
                </Paragraph>
              )}
              <Text type="secondary">
                {[
                  `${t('incident.detail.tasks.incidentResolver')}: ${issuingPartyResolution.actorName || '-'}`,
                  `${t('incident.detail.tasks.incidentResolvedAt')}: ${new Date(issuingPartyResolution.resolvedAt).toLocaleString()}`,
                ].join(' · ')}
              </Text>
            </div>
          );
        }
        const hasIssuerFollowUp = hasOpenIssuerRequestForTask(task);
        return (
          <div className={hasIssuerFollowUp ? 'taskResolvedMessage taskResolvedMessageAttention' : 'taskResolvedMessage'}>
            <Text strong>
              {t(
                hasIssuerFollowUp
                  ? 'incident.detail.tasks.resolvedIssuerFollowUpTitle'
                  : 'incident.detail.tasks.resolvedWaitingIssuerTitle'
              )}
            </Text>
            <Text type="secondary">
              {t(
                hasIssuerFollowUp
                  ? 'incident.detail.tasks.resolvedIssuerFollowUpDescription'
                  : 'incident.detail.tasks.resolvedWaitingIssuerDescription'
              )}
            </Text>
          </div>
        );
      }
      return (
        <>
          {sourceRequestSummary}
          {renderTaskInputLanguageHint(task)}
          <TextArea
            rows={3}
            disabled={!editable}
            value={task.root_cause || ''}
            onChange={(event) => handleTaskFieldChange(task.id, { root_cause: event.target.value })}
            placeholder={t('incident.detail.tasks.rootCause')}
          />
          <TextArea
            rows={3}
            disabled={!editable}
            value={task.response_summary || ''}
            onChange={(event) => handleTaskFieldChange(task.id, { response_summary: event.target.value })}
            placeholder={t('incident.detail.tasks.responseSummaryRequiredPlaceholder')}
            status={!task.response_summary?.trim() ? 'warning' : undefined}
          />
        </>
      );
    }

    return (
      <>
        {sourceRequestSummary}
        {renderTaskInputLanguageHint(task)}
        <TextArea
          rows={3}
          disabled={!editable}
          value={taskResponseDrafts[task.id] || ''}
          onChange={(event) => handleTaskResponseDraftChange(task.id, event.target.value)}
          placeholder={t('incident.detail.tasks.answer')}
        />
        <div className="taskAnswerGrid">{finalResponseDueDate}</div>
      </>
    );
  };

  const renderRequestCard = (request: IncidentRequest, currentTask: IncidentTask) => {
    const isIncoming = request.target_role === currentTask.responsible_role;
    const isSourceTask = request.source_task_id === currentTask.id;
    const draft = requestDrafts[request.id] || '';
    return (
      <div className="incidentRequestCard" key={request.id}>
        <div className="incidentRequestHeader">
          <Space wrap>
            <Tag color={isIncoming ? 'blue' : 'geekblue'}>
              {isIncoming ? t('incident.detail.requests.incoming') : t('incident.detail.requests.outgoing')}
            </Tag>
            <Tag>{t(`incident.requestType.${request.request_type}`)}</Tag>
            {request.edit_count > 0 && (
              <Button size="small" type="link" onClick={() => openAuditHistory({ type: 'request', request })}>
                {t('incident.detail.audit.edited')}
              </Button>
            )}
          </Space>
          <Space wrap>
            <Text type="secondary">{new Date(request.created_at).toLocaleString()}</Text>
          </Space>
        </div>
        {request.title && (
          <Text strong>{requestDisplayTitle(request)}</Text>
        )}
        <Paragraph className="taskActivityBody">
          {renderLocalizedText('incident_request', request.id, 'requested_action', request.requested_action)}
        </Paragraph>
        <Space wrap size={[8, 4]}>
          <Text type="secondary">{`${t('incident.detail.requests.from')}: ${t(`incident.role.${request.source_role}`)}`}</Text>
          <Text type="secondary">{`${t('incident.detail.requests.to')}: ${t(`incident.role.${request.target_role}`)}`}</Text>
          {request.due_date && <Text type="secondary">{`${t('incident.detail.requests.dueDate')}: ${request.due_date}`}</Text>}
        </Space>
        {request.request_type === 'PARTS_RETURN' && (
          <Descriptions className="requestPartsMeta" size="small" column={2}>
            <Descriptions.Item label={t('incident.detail.requests.partName')}>{request.part_name || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('incident.detail.requests.partNumber')}>{request.part_number || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('incident.detail.requests.quantity')}>{request.quantity || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('incident.detail.requests.shipTo')}>
              {renderLocalizedText('incident_request', request.id, 'ship_to', request.ship_to)}
            </Descriptions.Item>
            <Descriptions.Item label={t('incident.detail.requests.trackingNumber')}>{request.tracking_number || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('incident.detail.requests.receivedAt')}>{request.received_at || '-'}</Descriptions.Item>
            <Descriptions.Item label={t('incident.detail.requests.returnReason')} span={2}>
              {renderLocalizedText('incident_request', request.id, 'return_reason', request.return_reason)}
            </Descriptions.Item>
          </Descriptions>
        )}
        {request.activities.length > 0 && (
          <div className="incidentRequestActivityList">
            {request.activities.map((activity) => (
              <div className="incidentRequestActivity" key={activity.id}>
                <Space wrap>
                  <Text strong>{t(`incident.requestActivity.${activity.activity_type}`)}</Text>
                  {activity.edit_count > 0 && (
                    <Button size="small" type="link" onClick={() => openAuditHistory({ type: 'requestActivity', request, activity })}>
                      {t('incident.detail.audit.edited')}
                    </Button>
                  )}
                  {hasEditableRequestActivityContent(activity) && canManageAuditItem(activity.actor_user_id) && (
                    <Button size="small" onClick={() => openAuditEditor({ type: 'requestActivity', request, activity })}>
                      {t('incident.detail.actions.edit')}
                    </Button>
                  )}
                </Space>
                <Text type="secondary">
                  {[
                    activity.actor_display_name,
                    activity.actor_role ? t(`incident.role.${activity.actor_role}`) : null,
                    new Date(activity.created_at).toLocaleString(),
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
                {activity.body && (
                  <Paragraph className="taskActivityBody">
                    {renderLocalizedText('incident_request_activity', activity.id, 'body', activity.body)}
                  </Paragraph>
                )}
              </div>
            ))}
          </div>
        )}
        {canUpdateRequest(request) && request.status !== 'CLOSED' && request.status !== 'CANCELLED' && (
          <div className="incidentRequestActions">
            {isIncoming && canAnswerRequest(request) && request.status !== 'RESOLVED' && (
              <>
                <Input
                  value={draft}
                  onChange={(event) => setRequestDrafts((current) => ({ ...current, [request.id]: event.target.value }))}
                  placeholder={t('incident.detail.requests.responsePlaceholder')}
                />
                <Button
                  loading={requestActionId === request.id}
                  onClick={() => handleUpdateRequestStatus(request, 'IN_PROGRESS', draft)}
                >
                  {t('incident.detail.requests.markInProgress')}
                </Button>
                <Button
                  disabled={!draft.trim()}
                  loading={requestActionId === request.id}
                  type="primary"
                  onClick={() => handleUpdateRequestStatus(request, 'RESOLVED', draft)}
                >
                  {t('incident.detail.requests.resolve')}
                </Button>
              </>
            )}
            {isSourceTask && canCloseRequest(request) && (
              <>
                <Button
                  loading={requestActionId === request.id}
                  onClick={() => handleUpdateRequestStatus(request, 'IN_PROGRESS', draft)}
                >
                  {t('incident.detail.requests.reopen')}
                </Button>
                <Button
                  loading={requestActionId === request.id}
                  type="primary"
                  onClick={() => handleUpdateRequestStatus(request, 'CLOSED', draft)}
                >
                  {t('incident.detail.requests.confirmClose')}
                </Button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderTaskItem = (task: IncidentTask) => {
    const pendingRequestReview = hasPendingRequestReview(task);
    const resolvedPrimaryResponseTask = task.status === 'RESOLVED' && isPrimaryResponseTask(task) && Boolean(task.resolved_at);
    const pendingIssuingPartyConfirmationClose =
      task.status === 'CLOSED' && isIssuingPartyConfirmationTask(task) && !task.closed_at;
    const availableTaskStatusOptions = taskStatusOptions.filter(
      (status) =>
        !(status === 'OPEN' && task.status !== 'OPEN') &&
        !(status === 'CLOSED' && isPrimaryResponseTask(task)) &&
        !(status === 'RESOLVED' && isIssuingPartyConfirmationTask(task))
    );
    const taskStatusLabel = (status: IncidentTaskStatus) =>
      status === 'CLOSED' && isIssuingPartyConfirmationTask(task)
        ? t('incident.detail.tasks.confirmationClosedStatus')
        : t(`incident.taskStatus.${status}`);
    return (
      <List.Item key={task.id}>
        <div className="taskItem">
          <div className="taskHeader">
            <Space wrap>
              <Text type="secondary">{t(`incident.taskType.${task.task_type}`)}</Text>
              {task.status === 'CLOSED' && isIssuingPartyConfirmationTask(task) ? (
                <Tag>{t('incident.detail.tasks.confirmationClosedStatus')}</Tag>
              ) : (
                <IncidentTaskStatusTag status={task.status} />
              )}
            </Space>
            {(task.status !== 'CLOSED' || pendingIssuingPartyConfirmationClose) && !pendingRequestReview && !resolvedPrimaryResponseTask && (
              <Space wrap>
                <Select
                  className="taskStatusSelect"
                  loading={taskSavingId === task.id}
                  disabled={!canEditTask(task)}
                  value={task.status}
                  onChange={(status) => handleTaskStatusChange(task.id, status)}
                  options={availableTaskStatusOptions.map((status) => ({
                    value: status,
                    label: taskStatusLabel(status),
                  }))}
                />
                <Button disabled={!canEditTask(task)} loading={taskSavingId === task.id} onClick={() => handleTaskSave(task)}>
                  {t('incident.detail.actions.saveTask')}
                </Button>
              </Space>
            )}
          </div>
          {renderTaskResponseFields(task)}
          {canEditTask(task) && task.status !== 'CLOSED' && !pendingRequestReview && !resolvedPrimaryResponseTask && (
            <Text type="secondary" className="taskRuleText">
              {task.status === 'ON_HOLD'
                ? t('incident.detail.tasks.onHoldRule')
                : task.status === 'RESOLVED'
                  ? t('incident.detail.tasks.resolvedRule')
                  : t('incident.detail.tasks.defaultRule')}
            </Text>
          )}
          {renderTaskTimeline(task)}
        </div>
      </List.Item>
    );
  };

  const getTaskRequestEntries = (roleTasks: IncidentTask[]) => {
    const roleTaskIds = new Set(roleTasks.map((task) => task.id));
    const role = roleTasks[0]?.responsible_role;
    return requests
      .filter(
        (request) =>
          (request.target_task_id ? roleTaskIds.has(request.target_task_id) : request.target_role === role)
      )
      .map((request) => {
        const currentTask =
          roleTasks.find((task) => task.id === request.target_task_id) ||
          roleTasks.find((task) => task.responsible_role === request.target_role) ||
          roleTasks[0];
        return currentTask ? { request, currentTask } : null;
      })
      .filter((entry): entry is { request: IncidentRequest; currentTask: IncidentTask } => Boolean(entry));
  };

  const renderTaskRequestPanel = (roleTasks: IncidentTask[]) => {
    const requestEntries = getTaskRequestEntries(roleTasks);
    return (
      <Card
        className="taskRequestPane"
        title={(
          <Space size={8}>
            <span>{t('incident.detail.requests.panelTitle')}</span>
            <Tooltip title={t('help.contextHelp')}>
              <Typography.Link href="/help/related-requests" target="_blank" rel="noreferrer" aria-label={t('help.contextHelp')}>
                <QuestionCircleOutlined />
              </Typography.Link>
            </Tooltip>
          </Space>
        )}
      >
        {requestEntries.length > 0 ? (
          <div className="incidentRequestList">
            {requestEntries.map(({ request, currentTask }) => renderRequestCard(request, currentTask))}
          </div>
        ) : (
          <Empty className="incidentRequestEmpty" image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('incident.detail.requests.empty')} />
        )}
      </Card>
    );
  };

  const renderInternalCommentCard = (task: IncidentTask) => {
    const internalComments = taskInternalComments[task.id] || [];
    const body = taskInternalCommentBodies[task.id] || '';
    return (
      <Card
        className="taskInternalCommentPane"
        title={t('incident.detail.sections.internalComments')}
        extra={
          <Text type="secondary">
            {internalComments.length}{' '}
            {t(internalComments.length === 1 ? 'incident.detail.comments.countSingular' : 'incident.detail.comments.countPlural')}
          </Text>
        }
      >
        <Paragraph className="commentGuidance" type="secondary">
          {t('incident.detail.internalComments.guidance')}
        </Paragraph>
        <List
          locale={{ emptyText: t('incident.detail.internalComments.empty') }}
          dataSource={internalComments}
          renderItem={(comment) => {
            const canManage =
              comment.author_user_id === currentUser?.id ||
              currentUser?.roles.some((role) => adminRoles.includes(role)) ||
              currentUser?.assignments.some((assignment) => adminRoles.includes(assignment.role));
            const isEditing = editingTaskInternalCommentId === comment.id;
            return (
              <List.Item
                actions={
                  canManage
                    ? [
                        <Button
                          key="edit"
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          disabled={Boolean(editingTaskInternalCommentId && !isEditing)}
                          onClick={() => startEditTaskInternalComment(comment)}
                        >
                          {t('incident.detail.actions.edit')}
                        </Button>,
                        <Button
                          key="delete"
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={taskInternalCommentActionId === comment.id && !isEditing}
                          disabled={Boolean(editingTaskInternalCommentId)}
                          onClick={() => handleDeleteTaskInternalComment(task, comment.id)}
                        >
                          {t('incident.detail.actions.delete')}
                        </Button>,
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  avatar={renderCommentAvatar(comment)}
                  title={
                    <Space className="commentMetaTitle" size={8}>
                      <Text strong>{getCommentAuthorName(comment)}</Text>
                      <Text type="secondary">{new Date(comment.created_at).toLocaleString()}</Text>
                      {comment.edited_at && <Text type="secondary">{t('incident.detail.comments.edited')}</Text>}
                    </Space>
                  }
                  description={
                    isEditing ? (
                      <Space.Compact className="commentEditComposer">
                        <Input
                          value={editingTaskInternalCommentBody}
                          maxLength={500}
                          showCount
                          onChange={(event) => setEditingTaskInternalCommentBody(event.target.value)}
                          onPressEnter={() => handleUpdateTaskInternalComment(task, comment.id)}
                        />
                        <Button
                          type="primary"
                          loading={taskInternalCommentActionId === comment.id}
                          onClick={() => handleUpdateTaskInternalComment(task, comment.id)}
                        >
                          {t('incident.detail.actions.save')}
                        </Button>
                        <Button onClick={cancelEditTaskInternalComment}>{t('common.cancel')}</Button>
                      </Space.Compact>
                    ) : (
                      <Paragraph className="commentBody">
                        {renderLocalizedText('incident_task_internal_comment', comment.id, 'body', comment.body)}
                      </Paragraph>
                    )
                  }
                />
              </List.Item>
            );
          }}
        />
        <Space.Compact className="commentComposer">
          <Input
            value={body}
            maxLength={500}
            showCount
            onChange={(event) => setTaskInternalCommentBodies((current) => ({ ...current, [task.id]: event.target.value }))}
            onPressEnter={() => handleAddTaskInternalComment(task)}
            placeholder={t('incident.detail.internalComments.placeholder')}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={taskInternalCommentSubmittingId === task.id}
            onClick={() => handleAddTaskInternalComment(task)}
          >
            {t('incident.detail.actions.post')}
          </Button>
        </Space.Compact>
      </Card>
    );
  };

  const isRequestOnlyIssuingPartyTask = (task: IncidentTask) =>
    task.task_type === 'ISSUING_PARTY_INFORMATION' &&
    requests.some(
      (request) =>
        request.request_type === 'INFORMATION' &&
        request.target_role === task.responsible_role &&
        (!request.target_task_id || request.target_task_id === task.id)
    );

  const renderRoleTaskTab = (role: UserRole) => {
    const roleTasks = tasks.filter((task) => task.responsible_role === role);
    const mainTasks = roleTasks.filter((task) => !isRequestOnlyIssuingPartyTask(task));
    const internalCommentTask = roleTasks.find(canViewTaskInternalComments);
    return (
      <div className="roleTaskLayout">
        <Card className="taskMainPane" title={t(`incident.detail.taskCards.${role}`)}>
          <List
            className="taskCardList"
            locale={{ emptyText: t('incident.detail.tasks.empty') }}
            dataSource={mainTasks}
            renderItem={renderTaskItem}
          />
          {mainTasks.length < roleTasks.length && (
            <Text type="secondary" className="taskRuleText">
              {t('incident.detail.tasks.requestOnlyTaskHidden')}
            </Text>
          )}
        </Card>
        {internalCommentTask && renderInternalCommentCard(internalCommentTask)}
        {renderTaskRequestPanel(roleTasks)}
      </div>
    );
  };

  const handleActivitySelect = (activityId: string) => {
    const activity = taskActivities.find((item) => item.id === activityId);
    const task = activity ? tasks.find((item) => item.id === activity.task_id) : null;
    if (task) setActiveTabKey(`task-${task.responsible_role}`);
    window.setTimeout(() => {
      window.document.getElementById(`task-activity-${activityId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
  };

  const uploadProps: UploadProps = {
    beforeUpload: async (file) => {
      if (!incident) return Upload.LIST_IGNORE;
      const attachment = await onAttachFile(incident.id, file);
      setAttachments((current) => [attachment, ...current.filter((item) => item.id !== attachment.id)]);
      return Upload.LIST_IGNORE;
    },
    showUploadList: false,
  };

  const setAuditField = (field: string, value: string) => {
    setAuditEditValues((current) => ({ ...current, [field]: value }));
  };

  const auditFieldLabel = (field: string) =>
    t(`incident.detail.audit.fields.${field}`, { defaultValue: t(`incident.detail.requests.${field}`, { defaultValue: field }) });

  const renderAuditEditorFields = () => {
    if (!auditEditTarget) return null;
    if (auditEditTarget.type === 'taskActivity') {
      const fields = getEditableTaskActivityFields(auditEditTarget.activity);
      return (
        <>
          {fields.map((field) => (
            <TextArea
              key={field}
              rows={field === 'body' || field === 'requested_action' ? 3 : 2}
              value={auditEditValues[field] || ''}
              onChange={(event) => setAuditField(field, event.target.value)}
              placeholder={auditFieldLabel(field)}
            />
          ))}
        </>
      );
    }
    if (auditEditTarget.type === 'request') {
      const isPartsReturn = auditEditTarget.request.request_type === 'PARTS_RETURN';
      return (
        <>
          <Input value={auditEditValues.title || ''} onChange={(event) => setAuditField('title', event.target.value)} placeholder={auditFieldLabel('title')} />
          <TextArea rows={3} value={auditEditValues.requested_action || ''} onChange={(event) => setAuditField('requested_action', event.target.value)} placeholder={auditFieldLabel('requested_action')} />
          {isPartsReturn && (
            <div className="auditEditGrid">
              <Input value={auditEditValues.part_name || ''} onChange={(event) => setAuditField('part_name', event.target.value)} placeholder={auditFieldLabel('part_name')} />
              <Input value={auditEditValues.part_number || ''} onChange={(event) => setAuditField('part_number', event.target.value)} placeholder={auditFieldLabel('part_number')} />
              <Input type="number" min={1} value={auditEditValues.quantity || ''} onChange={(event) => setAuditField('quantity', event.target.value)} placeholder={auditFieldLabel('quantity')} />
              <Input value={auditEditValues.ship_to || ''} onChange={(event) => setAuditField('ship_to', event.target.value)} placeholder={auditFieldLabel('ship_to')} />
              <TextArea rows={2} value={auditEditValues.return_reason || ''} onChange={(event) => setAuditField('return_reason', event.target.value)} placeholder={auditFieldLabel('return_reason')} />
              <TextArea rows={2} value={auditEditValues.shipping_instruction || ''} onChange={(event) => setAuditField('shipping_instruction', event.target.value)} placeholder={auditFieldLabel('shipping_instruction')} />
              <Input value={auditEditValues.tracking_number || ''} onChange={(event) => setAuditField('tracking_number', event.target.value)} placeholder={auditFieldLabel('tracking_number')} />
              <Input value={auditEditValues.shipped_at || ''} onChange={(event) => setAuditField('shipped_at', event.target.value)} placeholder={auditFieldLabel('shipped_at')} />
              <Input value={auditEditValues.received_at || ''} onChange={(event) => setAuditField('received_at', event.target.value)} placeholder={auditFieldLabel('received_at')} />
            </div>
          )}
        </>
      );
    }
    return (
      <TextArea rows={3} value={auditEditValues.body || ''} onChange={(event) => setAuditField('body', event.target.value)} placeholder={auditFieldLabel('body')} />
    );
  };

  const renderAuditValues = (values: Record<string, unknown>) => (
    <Descriptions size="small" column={1} bordered>
      {Object.entries(values).map(([field, value]) => (
        <Descriptions.Item key={field} label={auditFieldLabel(field)}>
          {value === null || value === undefined || value === '' ? '-' : String(value)}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );

  const handleToggleWatch = async () => {
    if (!incident) return;
    setReactionSaving('watch');
    try {
      await onSetIncidentWatch(incident.id, !incident.is_watched_by_me);
    } finally {
      setReactionSaving(null);
    }
  };

  const handleToggleUseful = async () => {
    if (!incident) return;
    setReactionSaving('useful');
    try {
      await onSetIncidentUseful(incident.id, !incident.is_marked_useful_by_me);
    } finally {
      setReactionSaving(null);
    }
  };

  const renderAISummary = (item: Incident) => {
    const summary = item.ai_summary;
    if (!summary) {
      return (
        <div className="aiSummaryEmpty">
          <Text type="secondary">{t('incident.detail.aiSummary.notGenerated')}</Text>
        </div>
      );
    }
    const isUpdating = summary.status === 'PENDING' || summary.status === 'STALE';
    const statusLabel = t(`incident.detail.aiSummary.status.${summary.status}`);
    const localizedSummary = localized('incident_ai_summary', item.id, 'summary_text', summary.summary_text);
    return (
      <div className="aiSummaryBody">
        <Space className="aiSummaryMeta" wrap>
          <Tag color={summary.status === 'READY' ? 'blue' : summary.status === 'FAILED' ? 'red' : 'gold'}>
            {statusLabel}
          </Tag>
          {summary.generated_at && (
            <Text type="secondary">
              {t('incident.detail.aiSummary.generatedAt', { datetime: new Date(summary.generated_at).toLocaleString() })}
            </Text>
          )}
          {summary.provider_model && <Text type="secondary">{summary.provider_model}</Text>}
        </Space>
        {isUpdating && summary.summary_text && (
          <Text className="aiSummaryNotice" type="secondary">
            {t('incident.detail.aiSummary.updatingWithPrevious')}
          </Text>
        )}
        {summary.status === 'FAILED' && (
          <Text className="aiSummaryNotice" type="danger">
            {t('incident.detail.aiSummary.failed')}
          </Text>
        )}
        {summary.summary_text ? (
          <Paragraph className="aiSummaryText">
            <Space direction="vertical" size={4} className="localizedTextBlock">
              <span>{localizedSummary.text}</span>
              {localizedSummary.is_translated && (
                <Space size={6} className="translationMeta">
                  <Tag color="blue">{t('translation.autoTranslated')}</Tag>
                  <details>
                    <summary>{t('translation.showOriginal')}</summary>
                    <Text type="secondary">{localizedSummary.original_text}</Text>
                  </details>
                </Space>
              )}
              {!localizedSummary.is_translated && localizedSummary.translation_status === 'PENDING' && (
                <Tag>{t('translation.pending')}</Tag>
              )}
            </Space>
          </Paragraph>
        ) : (
          <Text type="secondary">{t('incident.detail.aiSummary.pending')}</Text>
        )}
      </div>
    );
  };

  if (!incident) {
    return (
      <div className="page">
        <Empty description={t('incident.detail.notFound')} />
      </div>
    );
  }

  return (
    <div className="page incidentDetailPage">
      {messageContextHolder}
      <div className="pageHeader">
        <div className="incidentDetailTitleRow">
          <h2>{localizedPlainText('incident', incident.id, 'title', incident.title)}</h2>
          <Button type="primary" disabled={!canEditIncidentBody} onClick={openEditDrawer}>
            {t('incident.detail.actions.edit')}
          </Button>
        </div>
        <Space className="pageMetaBar" wrap>
          <Text type="secondary">{incident.incident_number}</Text>
          <IncidentStatusTag status={incident.status} />
          <IncidentSeverityTag severity={incident.severity} />
          <Button
            className={incident.is_watched_by_me ? 'incidentReactionButton isActive' : 'incidentReactionButton'}
            icon={<BellOutlined />}
            loading={reactionSaving === 'watch'}
            onClick={handleToggleWatch}
            size="small"
          >
            {incident.is_watched_by_me ? t('incident.detail.reactions.watching') : t('incident.detail.reactions.watch')}
            {incident.watcher_count > 0 ? ` ${incident.watcher_count}` : ''}
          </Button>
          <Tooltip title={t('incident.detail.reactions.usefulTooltip')}>
            <Button
              className={incident.is_marked_useful_by_me ? 'incidentReactionButton isActive' : 'incidentReactionButton'}
              icon={<LikeOutlined />}
              loading={reactionSaving === 'useful'}
              onClick={handleToggleUseful}
              size="small"
            >
              {t('incident.detail.reactions.useful')}
              {incident.useful_count > 0 ? ` ${incident.useful_count}` : ''}
            </Button>
          </Tooltip>
        </Space>
      </div>
      <Tabs
        className="incidentDetailTabs"
        activeKey={activeTabKey}
        onChange={setActiveTabKey}
        items={[
          {
            key: 'details',
            label: t('incident.detail.tabs.details'),
            children: (
              <div className="detailTabStack">
      <Card title={t('incident.detail.processTimeline.title')}>
        <ProcessTimelineChart timeline={processTimeline} onActivitySelect={handleActivitySelect} />
      </Card>
      <Card title={t('incident.detail.aiSummary.title')}>
        {renderAISummary(incident)}
      </Card>
      <Card title={t('incident.detail.sections.fieldChanges')}>
        {renderFieldChanges()}
      </Card>
      <Card title={t('incident.detail.sections.summary')}>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label={t('incident.create.fields.troubleDate')}>{incident.trouble_date}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.claimReceiveDate')}>{incident.claim_receive_date}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.commissioningDate')}>{incident.commissioning_date || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.infoClassification')}>{incident.info_classification}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.claimClass')}>
            {translateKnownValue('incident.claimClass', incident.claim_class)}
          </Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.productClassification')}>{incident.product_classification}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.defectLocation')}>{incident.defect_location || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.defectLocationTags')}>{renderDefectTags('LOCATION')}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.defectPhenomenonTags')}>{renderDefectTags('PHENOMENON')}</Descriptions.Item>
          <Descriptions.Item label={t('incident.detail.fields.model')}>{incident.model_name || incident.model_number || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.serialNo')}>{incident.serial_no || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.detail.fields.lot')}>{incident.lot_number || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.siteSolved')}>
            {translateKnownValue('incident.siteSolved', incident.site_resolution_status)}
          </Descriptions.Item>
          <Descriptions.Item label={t('incident.list.columns.updated')}>{new Date(incident.updated_at).toLocaleString()}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title={t('incident.detail.sections.phenomenon')}>
        {renderLocalizedText('incident', incident.id, 'phenomenon', incident.phenomenon, { paragraph: true })}
      </Card>
      <Card title={t('incident.detail.sections.initialComment')}>
        {renderLocalizedText('incident', incident.id, 'initial_comment', incident.initial_comment, { paragraph: true })}
      </Card>
      <Card title={t('incident.create.fields.customerImpact')}>
        {renderLocalizedText('incident', incident.id, 'customer_impact', incident.customer_impact, { paragraph: true })}
      </Card>
      <Card title={t('incident.create.fields.temporaryAction')}>
        {renderLocalizedText('incident', incident.id, 'temporary_action', incident.temporary_action, { paragraph: true })}
      </Card>
      {renderProductFormSnapshot()}
      <Card title={t('incident.detail.sections.equipmentEnvironment')}>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label={t('incident.create.fields.relatedGibNo')}>{incident.related_gib_no || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.newProductFlag')}>{incident.is_new_product ? t('common.yes') : t('common.no')}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.unitCount')}>{incident.unit_count ?? '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.productionFactory')}>{incident.production_factory || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.productionFactorySettingDate')}>{incident.production_factory_setting_date || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.season')}>
            {translateKnownValue('incident.season', incident.season)}
          </Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.pipingLength')}>{incident.piping_length || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.errorCode')}>{incident.error_code || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.roomSize')}>{incident.room_size || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card title={t('incident.detail.sections.channel')}>
        <Descriptions bordered column={1} size="small">
          <Descriptions.Item label={t('incident.create.fields.dealer')}>{incident.dealer || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.deliveryDestination')}>{incident.delivery_destination || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.installerName')}>{incident.installer_name || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.deliveryRoute')}>{incident.delivery_route || '-'}</Descriptions.Item>
          <Descriptions.Item label={t('incident.create.fields.serviceControlNo')}>{incident.service_control_no || '-'}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Card
        title={t('incident.detail.sections.comments')}
        extra={
          <Text type="secondary">
            {comments.length} {t(comments.length === 1 ? 'incident.detail.comments.countSingular' : 'incident.detail.comments.countPlural')}
          </Text>
        }
      >
        <Paragraph className="commentGuidance" type="secondary">
          {t('incident.detail.comments.guidance')}
        </Paragraph>
        <List
          locale={{ emptyText: t('incident.detail.comments.empty') }}
          dataSource={comments}
          renderItem={(comment) => {
            const canManage =
              comment.author_user_id === currentUser?.id ||
              currentUser?.roles.some((role) => adminRoles.includes(role)) ||
              currentUser?.assignments.some((assignment) => adminRoles.includes(assignment.role));
            const isEditing = editingCommentId === comment.id;
            return (
              <List.Item
                actions={
                  canManage
                    ? [
                        <Button
                          key="edit"
                          type="text"
                          size="small"
                          icon={<EditOutlined />}
                          disabled={Boolean(editingCommentId && !isEditing)}
                          onClick={() => startEditComment(comment)}
                        >
                          {t('incident.detail.actions.edit')}
                        </Button>,
                        <Button
                          key="delete"
                          type="text"
                          danger
                          size="small"
                          icon={<DeleteOutlined />}
                          loading={commentActionId === comment.id && !isEditing}
                          disabled={Boolean(editingCommentId)}
                          onClick={() => handleDeleteComment(comment.id)}
                        >
                          {t('incident.detail.actions.delete')}
                        </Button>,
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  avatar={renderCommentAvatar(comment)}
                  title={
                    <Space className="commentMetaTitle" size={8}>
                      <Text strong>{getCommentAuthorName(comment)}</Text>
                      <Text type="secondary">{new Date(comment.created_at).toLocaleString()}</Text>
                      {comment.edited_at && <Text type="secondary">{t('incident.detail.comments.edited')}</Text>}
                    </Space>
                  }
                  description={
                    isEditing ? (
                      <Space.Compact className="commentEditComposer">
                        <Input
                          value={editingCommentBody}
                          maxLength={500}
                          showCount
                          onChange={(event) => setEditingCommentBody(event.target.value)}
                          onPressEnter={() => handleUpdateComment(comment.id)}
                        />
                        <Button
                          type="primary"
                          loading={commentActionId === comment.id}
                          onClick={() => handleUpdateComment(comment.id)}
                        >
                          {t('incident.detail.actions.save')}
                        </Button>
                        <Button onClick={cancelEditComment}>{t('common.cancel')}</Button>
                      </Space.Compact>
                    ) : (
                      <Paragraph className="commentBody">
                        {renderLocalizedText('incident_comment', comment.id, 'body', comment.body)}
                      </Paragraph>
                    )
                  }
                />
              </List.Item>
            );
          }}
        />
        <Space.Compact className="commentComposer">
          <Input
            value={commentBody}
            maxLength={500}
            showCount
            onChange={(event) => setCommentBody(event.target.value)}
            onPressEnter={handleAddComment}
            placeholder={t('incident.detail.comments.placeholder')}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={commentSubmitting}
            onClick={handleAddComment}
          >
            {t('incident.detail.actions.post')}
          </Button>
        </Space.Compact>
      </Card>
      <Card
        title={t('incident.detail.sections.attachments')}
        extra={
          <Upload {...uploadProps}>
            <Button icon={<PaperClipOutlined />}>{t('incident.detail.actions.attachFile')}</Button>
          </Upload>
        }
      >
        <List
          locale={{ emptyText: t('incident.detail.attachments.empty') }}
          dataSource={attachments}
          renderItem={(attachment) => (
            <List.Item className="attachmentListItem">
              <div className="attachmentPreviewCard">
                <button
                  className="attachmentThumb"
                  type="button"
                  onClick={() => (canPreviewAttachment(attachment) ? handlePreviewAttachment(attachment) : handleDownloadAttachment(attachment))}
                  aria-label={attachment.file_name}
                >
                  {attachmentPreviewUrls[attachment.id] ? (
                    <img src={attachmentPreviewUrls[attachment.id]} alt={attachment.file_name} />
                  ) : (
                    <span className="attachmentThumbIcon">{attachmentIcon(attachment)}</span>
                  )}
                </button>
                <div className="attachmentMeta">
                  <Button
                    className="attachmentFileName"
                    type="link"
                    onClick={() => (canPreviewAttachment(attachment) ? handlePreviewAttachment(attachment) : handleDownloadAttachment(attachment))}
                  >
                    {attachment.file_name}
                  </Button>
                  <Space className="attachmentMetaDetails" direction="vertical" size={2}>
                    <Text type="secondary">
                      {t(`incident.attachmentCategory.${attachment.category}`)} · {formatBytes(attachment.size_bytes)} · {attachment.content_type}
                    </Text>
                    <Text type="secondary">
                      {[attachment.uploaded_by_display_name, new Date(attachment.created_at).toLocaleString()]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                    {isOfficeAttachment(attachment) && (
                      <Text type="secondary">{t('incident.detail.attachments.officeDownloadOnly')}</Text>
                    )}
                    {(attachment.thumbnail_status === 'PENDING' || attachment.thumbnail_status === 'PROCESSING') &&
                      (isImageAttachment(attachment) || isPdfAttachment(attachment)) && (
                        <Text type="secondary">{t('incident.detail.attachments.thumbnailProcessing')}</Text>
                      )}
                    {attachment.description && <Text type="secondary">{attachment.description}</Text>}
                  </Space>
                </div>
                <Space className="attachmentActions">
                  {canPreviewAttachment(attachment) && (
                    <Button icon={<EyeOutlined />} onClick={() => handlePreviewAttachment(attachment)}>
                      {t('incident.detail.actions.preview')}
                    </Button>
                  )}
                  <Button icon={<DownloadOutlined />} onClick={() => handleDownloadAttachment(attachment)}>
                    {t('incident.detail.actions.download')}
                  </Button>
                  {canDeleteAttachment(attachment) && (
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      loading={attachmentDeletingId === attachment.id}
                      onClick={() => handleDeleteAttachment(attachment)}
                    >
                      {t('incident.detail.actions.delete')}
                    </Button>
                  )}
                </Space>
              </div>
            </List.Item>
          )}
        />
      </Card>
              </div>
            ),
          },
          {
            key: 'task-ISSUING_PARTY',
            label: t('incident.detail.tabs.issuingPartyTask'),
            children: renderRoleTaskTab('ISSUING_PARTY'),
          },
          {
            key: 'task-SERVICE',
            label: t('incident.detail.tabs.serviceTask'),
            children: renderRoleTaskTab('SERVICE'),
          },
          {
            key: 'task-QUALITY',
            label: t('incident.detail.tabs.qualityTask'),
            children: renderRoleTaskTab('QUALITY'),
          },
        ]}
      />
      <Modal
        title={t('incident.detail.audit.editTitle', { title: getAuditTargetTitle(auditEditTarget) })}
        open={Boolean(auditEditTarget)}
        okText={t('incident.detail.actions.save')}
        cancelText={t('common.cancel')}
        confirmLoading={auditEditSaving}
        onCancel={closeAuditEditor}
        onOk={handleSaveAuditEdit}
      >
        <Space className="auditEditForm" direction="vertical" size={10}>
          {renderAuditEditorFields()}
          <TextArea
            rows={2}
            value={auditEditReason}
            onChange={(event) => setAuditEditReason(event.target.value)}
            placeholder={t('incident.detail.audit.reasonPlaceholder')}
            status={!auditEditReason.trim() ? 'warning' : undefined}
          />
        </Space>
      </Modal>
      <Modal
        title={t('incident.detail.audit.historyTitle', { title: getAuditTargetTitle(auditHistoryTarget) })}
        open={Boolean(auditHistoryTarget)}
        footer={null}
        onCancel={() => setAuditHistoryTarget(null)}
      >
        <List
          loading={auditHistoryLoading}
          locale={{ emptyText: t('incident.detail.audit.empty') }}
          dataSource={auditHistory}
          renderItem={(edit) => (
            <List.Item>
              <Space className="auditHistoryItem" direction="vertical" size={8}>
                <Text type="secondary">
                  {[edit.edited_by_display_name, new Date(edit.edited_at).toLocaleString()].filter(Boolean).join(' · ')}
                </Text>
                <Text>{edit.edit_reason}</Text>
                <Text strong>{t('incident.detail.audit.before')}</Text>
                {renderAuditValues(edit.before_values)}
                <Text strong>{t('incident.detail.audit.after')}</Text>
                {renderAuditValues(edit.after_values)}
              </Space>
            </List.Item>
          )}
        />
      </Modal>
      <Drawer
        destroyOnClose
        extra={
          <Space>
            <Button onClick={() => setEditOpen(false)}>{t('incident.create.actions.cancel')}</Button>
            <Button type="primary" loading={editSaving} onClick={() => form.submit()}>
              {t('incident.detail.actions.save')}
            </Button>
          </Space>
        }
        onClose={() => setEditOpen(false)}
        open={editOpen}
        title={t('incident.detail.edit.title')}
        width={840}
      >
        <Form form={form} layout="vertical" onFinish={handleEditFinish}>
          <Divider orientation="left">{t('incident.detail.edit.sections.basic')}</Divider>
          <Form.Item label={t('incident.create.fields.subject')} name="title" rules={[{ required: true }]}>
            <Input maxLength={200} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('incident.create.fields.troubleDate')}
                name="trouble_date"
                dependencies={['commissioning_date', 'claim_receive_date']}
                rules={[{ required: true }, { validator: validateEditDateOrder }]}
              >
                <DatePicker className="fullWidthControl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={t('incident.create.fields.claimReceiveDate')}
                name="claim_receive_date"
                dependencies={['commissioning_date', 'trouble_date']}
                rules={[{ required: true }, { validator: validateEditDateOrder }]}
              >
                <DatePicker className="fullWidthControl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.infoClassification')} name="info_classification" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'Field claim', label: t('incident.classification.fieldClaim') },
                    { value: 'Service request', label: t('incident.classification.serviceRequest') },
                    { value: 'Quality escalation', label: t('incident.classification.qualityEscalation') },
                    { value: 'Parts request', label: t('incident.classification.partsRequest') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.claimClass')} name="claim_class" rules={[{ required: true }]}>
                <Select
                  options={[
                    { value: 'standard', label: t('incident.claimClass.standard') },
                    { value: 'A', label: t('incident.claimClass.A') },
                    { value: 'S', label: t('incident.claimClass.S') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.severity')} name="severity" rules={[{ required: true }]}>
                <Select options={severityOptions.map((severity) => ({ value: severity, label: t(`incident.severity.${severity}`) }))} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.siteSolved')} name="site_resolution_status">
                <Select
                  allowClear
                  options={['Solved', 'Temporary recovery', 'Not solved', 'Unknown'].map((value) => ({
                    value,
                    label: t(`incident.siteSolved.${value}`),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label={t('incident.create.fields.relatedGibNo')} name="related_gib_no">
                <Input maxLength={80} />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">{t('incident.detail.edit.sections.content')}</Divider>
          <Form.Item label={t('incident.create.fields.initialComment')} name="initial_comment" rules={[{ required: true }]}>
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item label={t('incident.create.fields.defectPhenomena')} name="phenomenon" rules={[{ required: true }]}>
            <TextArea rows={5} />
          </Form.Item>
          <Form.Item label={t('incident.create.fields.temporaryAction')} name="temporary_action" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item label={t('incident.create.fields.customerImpact')} name="customer_impact">
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.defectLocation')} name="defect_location">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.defectLocationTags')} name="defect_location_tag_ids">
                <Select
                  allowClear
                  mode="multiple"
                  loading={defectTagsLoading}
                  options={defectLocationTagOptions}
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label={t('incident.create.fields.defectPhenomenonTags')} name="defect_phenomenon_tag_ids">
                <Select
                  allowClear
                  mode="multiple"
                  loading={defectTagsLoading}
                  options={defectPhenomenonTagOptions}
                  optionFilterProp="label"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">{t('incident.detail.edit.sections.equipment')}</Divider>
          <Form.Item label={t('incident.create.fields.installationEnvironment')} name="installation_environment" rules={[{ required: true }]}>
            <TextArea rows={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.productClassification')} name="product_classification" rules={[{ required: true }]}>
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.commissioningDate')} name="commissioning_date" dependencies={['trouble_date', 'claim_receive_date']} rules={[{ validator: validateEditDateOrder }]}>
                <DatePicker className="fullWidthControl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.modelName')} name="model_name" rules={[{ required: true }]}>
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.modelNumber')} name="model_number" rules={[{ required: true }]}>
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.serialNo')} name="serial_no" rules={[{ required: true }]}>
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.lotNumber')} name="lot_number" rules={[{ required: true }]}>
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.newProductFlag')} name="is_new_product">
                <Select
                  allowClear
                  options={[
                    { value: true, label: t('common.yes') },
                    { value: false, label: t('common.no') },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.unitCount')} name="unit_count">
                <InputNumber className="fullWidthControl" min={0} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.productionFactory')} name="production_factory">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.productionFactorySettingDate')} name="production_factory_setting_date">
                <DatePicker className="fullWidthControl" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.season')} name="season">
                <Select
                  allowClear
                  options={['Summer', 'Winter', 'All season', 'Off season'].map((value) => ({
                    value,
                    label: t(`incident.season.${value}`),
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.pipingLength')} name="piping_length">
                <Input maxLength={80} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.errorCode')} name="error_code">
                <Input maxLength={80} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.roomSize')} name="room_size">
                <Input maxLength={80} />
              </Form.Item>
            </Col>
          </Row>

          {incident?.product_form_schema_snapshot ? (
            <>
              <Divider orientation="left">{t('incident.detail.sections.productSpecific')}</Divider>
              <div className="productSpecificEditSection">
                <ProductFormRenderer schema={incident.product_form_schema_snapshot as IncidentFormSchema} />
              </div>
            </>
          ) : null}

          <Divider orientation="left">{t('incident.detail.edit.sections.channel')}</Divider>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.dealer')} name="dealer">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.deliveryDestination')} name="delivery_destination">
                <Input maxLength={240} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.installerName')} name="installer_name">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t('incident.create.fields.deliveryRoute')} name="delivery_route">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label={t('incident.create.fields.serviceControlNo')} name="service_control_no">
                <Input maxLength={120} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
      <Modal
        open={Boolean(previewAttachment)}
        title={previewAttachment?.file_name}
        width={920}
        footer={
          <Space>
            {previewAttachment && (
              <Button icon={<DownloadOutlined />} onClick={() => handleDownloadAttachment(previewAttachment)}>
                {t('incident.detail.actions.download')}
              </Button>
            )}
            <Button onClick={() => setPreviewAttachment(null)}>{t('common.cancel')}</Button>
          </Space>
        }
        onCancel={() => setPreviewAttachment(null)}
      >
        <div className="attachmentPreviewModal">
          {previewLoading ? (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('incident.detail.attachments.loadingPreview')} />
          ) : previewAttachment && previewUrl && isImageAttachment(previewAttachment) ? (
            <img className="attachmentPreviewImage" src={previewUrl} alt={previewAttachment.file_name} />
          ) : previewAttachment && previewUrl && isPdfAttachment(previewAttachment) ? (
            <iframe className="attachmentPreviewFrame" src={previewUrl} title={previewAttachment.file_name} />
          ) : previewAttachment && isTextAttachment(previewAttachment) ? (
            <pre className="attachmentPreviewText">{previewText || ''}</pre>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('incident.detail.attachments.previewUnavailable')} />
          )}
        </div>
      </Modal>
      <Modal
        title={t('incident.detail.close.title')}
        open={closeOpen}
        okText={t('incident.detail.actions.closeIncident')}
        cancelText={t('incident.create.actions.cancel')}
        confirmLoading={closeSaving}
        onCancel={() => setCloseOpen(false)}
        onOk={handleCloseIncident}
      >
        <TextArea
          rows={4}
          value={closeComment}
          onChange={(event) => setCloseComment(event.target.value)}
          placeholder={t('incident.detail.close.commentPlaceholder')}
          status={!closeComment.trim() ? 'warning' : undefined}
        />
      </Modal>
    </div>
  );
};

export default IncidentDetailPage;
